import store from '@/store/index.js'
import { parseCsv, CsvColumn } from '@/utils/csv/parse'
import { ScaleSheetImportFileType, ContractStatus, VehicleType, ScalingStatus, ScaleSheetTicketType, ContractType } from '@/utils/Enumerations.js'
import { getOriginationTicketId } from '@/utils/OriginationIdUtility.js'
import { groupBy, uniqBy } from 'lodash'
import i18n from '@/i18n'
import moment from 'moment'

const ALL_TICKET_BOOKS_FETCH_KEY = 0
const ALL_LOG_YARD_SALE_TICKETS_FETCH_KEY = 0
const NAME_TRAILER_IDENTIFIER_INDEX = 9
const MAX_NUMBER_OF_TICKETS = 500
const MAX_NUMBER_OF_LOGS_PER_TICKET = 500
const DIAMETER_LOWER_BOUND = 3
const DIAMETER_UPPER_BOUND = 60
const LENGTH_LOWER_BOUND = 6
const LENGTH_UPPER_BOUND = 40
const VOLUME_UPPER_BOUND = 6760
const MAX_REQUEST_STRING_LENGTH = 1500

export class ScaledTicketCreator {
  constructor (profile, file) {
    this.profile = profile
    this.scaleSheetFile = file
    this.ticketBooks = []
    this.contractsForTracts = new Map()
    this.tractPromises = new Map()
    this.contractsOnTractPromises = new Map()
    this.contractPromises = new Map()
    this.ticketBooksPromises = new Map()
    this.productPromises = new Map()
    this.activityPromises = new Map()
    this.ticketPromises = new Map()
    this.importErrors = null
    this.rawTickets = undefined
    this.skippedTicketContracts = []
  }

  async readFileAsString (file) {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.addEventListener('error', reject)
      reader.addEventListener('load', () => { resolve(reader.result) })
      reader.readAsText(file)
    })
  }

  async parseScaleSheet () {
    store.dispatch('global/setLoading', true)
    try {
      this.scaleSheetString = this.scaleSheetString ?? await this.readFileAsString(this.scaleSheetFile)
      if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.Csv.value) {
        return await this.createTicketsFromScaleSheetCsv()
      } else if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.Delimited.value) {
        return await this.createTicketsFromScaleSheetDelimited()
      } else if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.FixedLength.value) {
        return await this.createTicketsFromScaleSheetFixedLength()
      } else {
        return undefined
      }
    } finally {
      store.dispatch('global/setLoading', false)
    }
  }

  async parseRawTickets () {
    store.dispatch('global/setLoading', true)
    try {
      this.scaleSheetString = this.scaleSheetString ?? await this.readFileAsString(this.scaleSheetFile)
      if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.Csv.value) {
        this.rawTickets = this.parseRawTicketsFromCsv()
      } else if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.Delimited.value) {
        this.rawTickets = this.parseRawTicketsFromDelimited()
      } else if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.FixedLength.value) {
        this.rawTickets = await this.parseRawTicketsFromFixedLength()
      } else {
        this.rawTickets = undefined
      }
      return this.rawTickets
    } finally {
      store.dispatch('global/setLoading', false)
    }
  }

  async parseScaleSheetForSingleTicket (ticketId, extTicketNumber2, ticketNumber, contractKeys) {
    let tract
    let numericTicketNumber
    if (contractKeys.contractType === ContractType.Production.value || contractKeys.contractType === ContractType.WoodsSale.value) {
      const { tractId } = contractKeys
      this.ticketBooks = (await store.dispatch('ticket-books/fetchTicketBooks', { includeInUse: true, includeArchived: false, includeAvailable: false, pageNumbers: [Number(extTicketNumber2)] }))
        .filter(tb => tb.tractId === tractId)
      if (this.ticketBooks.length === 0) {
        store.dispatch('snackbar/setSnackError', i18n.t('noTicketBooksForAccountOnContract'))
        return undefined
      }
      numericTicketNumber = this.validateExtTicketNumber2ForSingleTicket(extTicketNumber2, tractId)
      if (numericTicketNumber === undefined) {
        return undefined
      }
      tract = await store.dispatch('tract/fetchTract', tractId)
    } else {
      numericTicketNumber = Number(ticketNumber)
    }
    if (!this.contractPromises.has(contractKeys.contractId)) {
      this.contractPromises.set(contractKeys.contractId, store.dispatch('contract/getContract', contractKeys.contractId))
    }
    this.scaleSheetString = await this.readFileAsString(this.scaleSheetFile)
    if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.Csv.value) {
      return await this.createVolumeInformationForSingleTicketCsv(ticketId, numericTicketNumber, tract, contractKeys)
    } else if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.Delimited.value) {
      return await this.createVolumeInformationForSingleTicketDelimited(ticketId, numericTicketNumber, tract, contractKeys)
    } else if (this.profile.scaleSheetImportFileType === ScaleSheetImportFileType.FixedLength.value) {
      return await this.createVolumeInformationForSingleTicketFixedLength(ticketId, numericTicketNumber, tract, contractKeys)
    } else {
      return undefined
    }
  }

  validateExtTicketNumber2ForSingleTicket (extTicketNumber2, tractId) {
    const numericTicketNumber = Number(extTicketNumber2)
    if (isNaN(numericTicketNumber)) {
      store.dispatch('snackbar/setSnackError', i18n.t('ticketNumberIsNotNumeric', { ticketNumber: extTicketNumber2 }))
      return undefined
    }
    const parsedTractId = this.ticketBooks.find(tb => tb.firstPage <= numericTicketNumber && tb.lastPage >= numericTicketNumber)?.tractId
    if (parsedTractId === undefined) {
      store.dispatch('snackbar/setSnackError', i18n.t('ticketNumberIsNotInTicketBook', { ticketNumber: extTicketNumber2 }))
      return undefined
    }
    if (parsedTractId !== tractId) {
      store.dispatch('snackbar/setSnackError', i18n.t('ticketNumberIsNotInTractTicketBook', { ticketNumber: extTicketNumber2 }))
      return undefined
    }
    return numericTicketNumber
  }

  async createTicketsFromScaleSheetCsv () {
    const groupedData = this.parseRawTicketsFromCsv()
    if (!groupedData) {
      return undefined
    }
    const tickets = await this.constructTicketContractInformationFromScaleSheet(groupedData)
    return tickets
  }

  async createTicketsFromScaleSheetDelimited () {
    const parsedContent = this.parseRawTicketsFromDelimited()
    if (!parsedContent) {
      return undefined
    }
    const tickets = await this.constructTicketContractInformationFromScaleSheet(parsedContent)
    return tickets
  }

  async createTicketsFromScaleSheetFixedLength () {
    const parsedContent = await this.parseRawTicketsFromFixedLength()
    if (!parsedContent) {
      return undefined
    }
    const tickets = await this.constructTicketContractInformationFromScaleSheet(parsedContent)
    return tickets
  }

  parseRawTicketsFromCsv () {
    const parsedCsv = parseCsv(this.scaleSheetString, this.parseFromCsvProfile(this.profile.scaleSheetImportCsvProfile), {
      throwOnUnusedColumns: false,
      missingColumnBehavior: 'ignore',
      strictRecordCount: false
    })
    if (parsedCsv?.data === undefined || parsedCsv?.data?.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorParsingCSV'))
      const errors = parsedCsv?.errors ?? []
      if (errors.length > 0) {
        this.importErrors = {
          errors: errors.map(e => ({ ticketNumber: 'N/A', message: e.message ?? JSON.stringify(e) })),
          invalidSegments: []
        }
      }
      return undefined
    }
    const groupedData = Object.entries(groupBy(parsedCsv.data, d => JSON.stringify([d.ticketNumber, d.tract, d.destination, d.setting, d.contractAccount, d.product]))).map(([jsonKey, ticketContent]) => {
      const [ticketNumber] = JSON.parse(jsonKey)
      return {
        ticketNumber,
        ticketContent: ticketContent.map((d, index) => ({
          ...d,
          logNumber: index + 1,
          hasScaleData: !(isNaN(d.segmentOneDiameter) && isNaN(d.segmentOneGrossVolume) && isNaN(d.segmentOneNetVolume) && isNaN(d.segmentOneLength) &&
            isNaN(d.segmentTwoDiameter) && isNaN(d.segmentTwoGrossVolume) && isNaN(d.segmentTwoNetVolume) && isNaN(d.segmentTwoLength) &&
            isNaN(d.segmentThreeDiameter) && isNaN(d.segmentThreeGrossVolume) && isNaN(d.segmentThreeNetVolume) && isNaN(d.segmentThreeLength))
        }))
      }
    })
    return groupedData
  }

  parseRawTicketsFromDelimited () {
    const ticketContent = this.getTicketDataForDelimitedData()
    if (!ticketContent) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorParsingScaleData'))
      return undefined
    }
    const parsedContent = this.parseFromDelimitedProfile(ticketContent)
    if (parsedContent.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInDelimitedFile'))
      return undefined
    }
    return parsedContent
  }

  async parseRawTicketsFromFixedLength () {
    const ticketContent = await this.getTicketDataForScaleSheetFixedLength()
    if (!ticketContent) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorParsingScaleData'))
      return undefined
    }
    const parsedContent = this.parseFromFixedLengthProfile(ticketContent)
    if (parsedContent.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInFixedLengthFile'))
      return undefined
    }
    return parsedContent
  }

  async createVolumeInformationForSingleTicketCsv (ticketId, ticketNumber, tract, contractKeys) {
    const parsedCsv = parseCsv(this.scaleSheetString, this.parseFromCsvProfile(this.profile.scaleSheetImportCsvProfile), {
      throwOnUnusedColumns: false,
      missingColumnBehavior: 'ignore',
      strictRecordCount: false
    })
    if (parsedCsv?.data === undefined || parsedCsv?.data?.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorParsingCSV'))
      return undefined
    }
    const groupedData = Object.entries(groupBy(parsedCsv.data, d => JSON.stringify([d.ticketNumber, d.tract, d.destination, d.setting, d.contractAccount, d.product]))).map(([jsonKey, ticketContent]) => {
      const [ticketNumber] = JSON.parse(jsonKey)
      return {
        ticketNumber,
        ticketContent: ticketContent.map((d, index) => ({
          ...d,
          logNumber: index + 1,
          hasScaleData: !(isNaN(d.segmentOneDiameter) && isNaN(d.segmentOneGrossVolume) && isNaN(d.segmentOneNetVolume) && isNaN(d.segmentOneLength) &&
            isNaN(d.segmentTwoDiameter) && isNaN(d.segmentTwoGrossVolume) && isNaN(d.segmentTwoNetVolume) && isNaN(d.segmentTwoLength) &&
            isNaN(d.segmentThreeDiameter) && isNaN(d.segmentThreeGrossVolume) && isNaN(d.segmentThreeNetVolume) && isNaN(d.segmentThreeLength))
        }))
      }
    })

    const ticketData = groupedData.find(tc => tc.ticketNumber === Number(ticketNumber))?.ticketContent

    if (!ticketData || ticketData?.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInCSV', { ticketNumber }))
      return undefined
    }

    const isValidContract = contractKeys.contractType === ContractType.LogYardSale.value || await this.validateContractInformationForSingleTicket(ticketData, contractKeys)
    if (!isValidContract) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorValidatingContractOn', { ticketNumber }))
      return undefined
    }

    const volumeInformationForTicket = await this.generateSegmentVolumes(ticketData)
    if (volumeInformationForTicket.invalidSegments.length > 0) {
      this.importErrors = {
        errors: volumeInformationForTicket.invalidSegments.map(is => ({ ticketNumber, message: is.message }))
      }
      store.dispatch('snackbar/setSnackError', i18n.t('invalidLogSegmentInformation', { ticketNumber }))
      return undefined
    }
    const contract = await this.contractPromises.get(contractKeys.contractId)
    const scalingRegion = contractKeys.contractType === ContractType.LogYardSale.value ? contract.scalingRegion : tract.scalingRegion
    return this.createVolumeDataForSingleTicket(ticketId, volumeInformationForTicket.validSegments, scalingRegion, 4) // TODO get right scale rule
  }

  async createVolumeInformationForSingleTicketDelimited (ticketId, ticketNumber, tract, contractKeys) {
    const ticketContent = this.getTicketDataForDelimitedData()
    if (!ticketContent) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorParsingScaleData'))
      return undefined
    }
    const parsedContent = this.parseFromDelimitedProfile(ticketContent)
    if (parsedContent.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInDelimitedFile'))
      return undefined
    }
    const ticketData = parsedContent.find(tc => tc.ticketNumber === Number(ticketNumber))?.ticketContent

    if (!ticketData || ticketData?.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInDelimitedFileForTicketNumber', { ticketNumber }))
      return undefined
    }
    const isValidContract = contractKeys.contractType === ContractType.LogYardSale.value || await this.validateContractInformationForSingleTicket(ticketData, contractKeys)
    if (!isValidContract) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorValidatingContractOn', { ticketNumber }))
      return undefined
    }

    const volumeInformationForTicket = await this.generateSegmentVolumes(ticketData)
    if (volumeInformationForTicket.invalidSegments.length > 0) {
      this.importErrors = {
        errors: volumeInformationForTicket.invalidSegments.map(is => ({ ticketNumber, message: is.message }))
      }
      store.dispatch('snackbar/setSnackError', i18n.t('invalidLogSegmentInformation', { ticketNumber }))
      return undefined
    }
    const contract = await this.contractPromises.get(contractKeys.contractId)
    const scalingRegion = contractKeys.contractType === ContractType.LogYardSale.value ? contract.scalingRegion : tract.scalingRegion
    return this.createVolumeDataForSingleTicket(ticketId, volumeInformationForTicket.validSegments, scalingRegion, 4) // TODO get right scale rule
  }

  async createVolumeInformationForSingleTicketFixedLength (ticketId, ticketNumber, tract, contractKeys) {
    const ticketContent = await this.getTicketDataForScaleSheetFixedLength()
    if (!ticketContent) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorParsingScaleData'))
      return undefined
    }
    const parsedContent = this.parseFromFixedLengthProfile(ticketContent)
    if (parsedContent.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInFixedLengthFile'))
      return undefined
    }
    const ticketData = parsedContent.find(tc => tc.ticketNumber === Number(ticketNumber))?.ticketContent
    if (!ticketData || ticketData?.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInFixedLengthFileForTicketNumber', { ticketNumber }))
      return undefined
    }
    const isValidContract = contractKeys.contractType === ContractType.LogYardSale.value || await this.validateContractInformationForSingleTicket(ticketData, contractKeys)
    if (!isValidContract) {
      store.dispatch('snackbar/setSnackError', i18n.t('errorValidatingContractOn', { ticketNumber }))
      return undefined
    }
    const volumeInformationForTicket = await this.generateSegmentVolumes(ticketData)
    if (volumeInformationForTicket.invalidSegments.length > 0) {
      this.importErrors = {
        errors: volumeInformationForTicket.invalidSegments.map(is => ({ ticketNumber, message: is.message }))
      }
      store.dispatch('snackbar/setSnackError', i18n.t('invalidLogSegmentInformation', { ticketNumber }))
      return undefined
    }
    const contract = await this.contractPromises.get(contractKeys.contractId)
    const scalingRegion = contractKeys.contractType === ContractType.LogYardSale.value ? contract.scalingRegion : tract.scalingRegion
    return this.createVolumeDataForSingleTicket(ticketId, volumeInformationForTicket.validSegments, scalingRegion, 4) // TODO get right scale rule
  }

  async validateContractInformationForSingleTicket (data, contractKeys) {
    const { contractId, tractId, contractAccountId, destinationAccountId, settingId, productId } = contractKeys
    const importedDestinationAccountId = this.profile.accountProfiles.find(ap => ap.externalAccountName === data[0].destination)?.accountId
    const importedSettingId = this.profile.settingProfiles.find(sp => sp.externalSettingName === data[0].setting)?.settingId
    const importedContractAccountId = this.profile.accountProfiles.find(ap => ap.externalAccountName === data[0].contractAccount)?.accountId
    const importedTractId = this.profile.tractProfiles.find(tp => tp.externalTractName === data[0].tract)?.tractId
    const importedProductId = this.profile.productProfiles.find(pp => pp.externalProductName === data[0].product)?.productId
    const contractsOnTract = await this.getContractsForTract(tractId)
    const contracts = contractsOnTract.filter(c => c.contractId === contractId &&
      c.status === ContractStatus.Open.value &&
      (importedContractAccountId ? c.accountId === importedContractAccountId && importedContractAccountId === contractAccountId : true) &&
      (importedDestinationAccountId ? c.destinationAccountId === importedDestinationAccountId && importedDestinationAccountId === destinationAccountId : true) &&
      (importedSettingId ? c.settingId === importedSettingId && importedSettingId === settingId : true) &&
      (importedTractId ? c.tractId === importedTractId && importedTractId === tractId : true) &&
      (importedProductId ? c.products.some(p => p.productId === importedProductId) && importedProductId === productId : true))
    return contracts.length > 0
  }

  async constructTicketContractInformationFromScaleSheet (tickets) {
    if (!this.ticketBooksPromises.has(ALL_TICKET_BOOKS_FETCH_KEY)) {
      const ticketNumbers = [...new Set(tickets.map(t => Number(t.ticketNumber)).filter(tn => !isNaN(tn)))]
      if (ticketNumbers.length === 0) {
        store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketNumbersFoundInScaleData'))
        return undefined
      }
      if (ticketNumbers.length > MAX_NUMBER_OF_TICKETS) {
        store.dispatch('snackbar/setSnackError', i18n.t('cannotImportMoreThanMaxTicketsAtATime', { max: MAX_NUMBER_OF_TICKETS }))
        return undefined
      }

      const ticketNumbersBatches = this.batchNumbersByUrlLength(ticketNumbers, MAX_REQUEST_STRING_LENGTH)

      const ticketBookFetchPromise = async fetchBooks => {
        const results = await Promise.all(fetchBooks.map(batch => store.dispatch('ticket-books/fetchTicketBooks', { includeInUse: true, includeArchived: false, includeAvailable: false, pageNumbers: batch })))
        const fetchedTicketBooks = results.flat()
        const ticketBooksById = groupBy(fetchedTicketBooks, tb => tb.ticketBookId)
        return Object.values(ticketBooksById).map(ticketBooks => {
          const [ticketBook] = ticketBooks
          const dedupedPages = Array.from(
            ticketBooks
              .flatMap(tb => tb.pages ?? [])
              .reduce((map, page) => {
                const pageKey = page.pageNumber
                if (!map.has(pageKey)) {
                  map.set(pageKey, page)
                }
                return map
              }, new Map())
              .values()
          ).sort((a, b) => a.pageNumber - b.pageNumber)
          return {
            ...ticketBook,
            pages: dedupedPages
          }
        })
      }
      this.ticketBooksPromises.set(ALL_TICKET_BOOKS_FETCH_KEY, ticketBookFetchPromise(ticketNumbersBatches))
    }
    this.ticketBooks = await this.ticketBooksPromises.get(ALL_TICKET_BOOKS_FETCH_KEY)
    const results = await Promise.all(tickets.map(async t => {
      const ticketNumber = t.ticketNumber
      const ticket = t.ticketContent
      const numericTicketNumber = Number(ticketNumber)
      if (isNaN(numericTicketNumber)) {
        return this.createPromiseResponse(false, i18n.t('ticketNumberIsNotNumeric', { ticketNumber }), ticketNumber)
      }

      const timeZoneOffset = store.getters['user/companyInfo'].timeZoneOffset * 60
      const weighedOutAt = ticket[0].weighedOutAt !== undefined && ticket[0].weighedOutAt !== null && ticket[0].weighedOutAt !== ''
        ? moment.utc(ticket[0].weighedOutAt).utcOffset(timeZoneOffset, true).set({ hour: 8, minute: 0, second: 0, millisecond: 0 }).utc()
        : moment.utc()

      if (weighedOutAt.isAfter(moment.utc().add(1, 'day').startOf('day'))) {
        return this.createPromiseResponse(false, i18n.t('ticketNumberWeighOutDateIsInTheFuture', { ticketNumber, weighedOutAt: weighedOutAt.format() }), ticketNumber)
      }

      const foundContracts = await this.findContractInformation(ticket[0])

      if (foundContracts.error) {
        return this.createPromiseResponse(false, foundContracts.error, ticketNumber)
      }

      const contracts = foundContracts.contracts

      const tractsOnContracts = [...new Set(contracts.map(c => c.tractId))].filter(t => t > 0)

      const alreadyScaledLogsContractIds = []
      if (tractsOnContracts.length === 1) {
        const tractId = tractsOnContracts[0]
        const ticketAlreadyCreatedId = await this.ticketBooks
          .filter(tb => tb.tractId === tractId)
          .find(tb => tb.firstPage <= numericTicketNumber && tb.lastPage >= numericTicketNumber)
          ?.pages.find(p => p.pageNumber === numericTicketNumber)?.ticketId

        const ticket = ticketAlreadyCreatedId ? await this.getTicketData(ticketAlreadyCreatedId) : undefined
        if (ticket && ticket.scalingStatus !== ScalingStatus.Ready.value) {
          alreadyScaledLogsContractIds.push(ticket.contractId)
        }
      } else if (tractsOnContracts.length > 1) {
        const possibleTicketBookPages = this.ticketBooks.filter(tb => tractsOnContracts.includes(tb.tractId) && tb.firstPage <= numericTicketNumber && tb.lastPage >= numericTicketNumber)
          .flatMap(tb => tb.pages)
          .filter(p => p.pageNumber === numericTicketNumber)
        const possibleTicketIds = possibleTicketBookPages.map(p => p.ticketId).filter(tid => tid)
        const fetchedTickets = await Promise.all(possibleTicketIds
          .map(async tid => await this.getTicketData(tid)))
        alreadyScaledLogsContractIds.push(...fetchedTickets.filter(t => t && t.scalingStatus !== ScalingStatus.Ready.value).map(t => t.contractId))
      }

      const alreadyScaledLogYardSaleContractId = (await this.ticketPromises.get(ALL_LOG_YARD_SALE_TICKETS_FETCH_KEY))
        ?.find(t => t.ticketNumber === numericTicketNumber && t.scalingStatus === ScalingStatus.Complete.value)?.contractId

      let segmentsSpecies = []
      let segmentGrades = []
      let volumeInformationForTicket
      if (ticket[0].hasScaleData) {
        volumeInformationForTicket = await this.generateSegmentVolumes(ticket)
        if (volumeInformationForTicket.invalidSegments.length > 0) {
          const messages = volumeInformationForTicket.invalidSegments.map(is => is.message)
          return this.createPromiseResponse(false, messages, ticketNumber)
        }
        segmentsSpecies = [...new Set(volumeInformationForTicket.validSegments.map(s => { return s.speciesId }))]
        segmentGrades = [...new Set(volumeInformationForTicket.validSegments.map(s => { return s.gradeId }))]
      }

      const products = await Promise.all([...new Set(contracts.flatMap(c => c.products.map(p => p.productId)))].map(async pid => {
        if (!this.productPromises.has(pid)) {
          this.productPromises.set(pid, store.dispatch('product/fetchProductById', { productId: pid }))
        }
        return await this.productPromises.get(pid)
      }))

      const productProfileId = this.profile.productProfiles.find(pp => pp.externalProductName === ticket[0].product)?.productId

      const mappedContracts = await Promise.all(contracts.map(async c => {
        if (!this.activityPromises.has(c.contractId)) {
          this.activityPromises.set(c.contractId, store.dispatch('activity/fetchActivities', c.contractId))
        }
        const activities = await this.activityPromises.get(c.contractId)
        const earliestEffectiveDate = activities.length > 0
          ? activities.reduce((earliest, a) => {
            const effectiveDate = moment.utc(a.effectiveDate)
            return effectiveDate.isBefore(earliest)
              ? effectiveDate
              : earliest
          }, moment.utc())
          : moment.utc()
        const productsOnContract = products.filter(p => c.products.some(cp => cp.productId === p.productId))
        const validProducts = productProfileId ? productsOnContract.filter(p => p.productId === productProfileId) : productsOnContract.filter(p => {
          const productHasSpecies = segmentsSpecies.every(ss => p.productSpecies.some(ps => ps.speciesId === ss))
          const productHasGrades = segmentGrades.every(sg => p.productGrades.some(pg => pg.gradeId === sg))
          return (productHasSpecies && productHasGrades) || (!p.isScalable && !ticket[0].hasScaleData)
        })
        return {
          ...c,
          products: validProducts.map(p => ({
            ...p,
            isFallback: c.products.find(cp => cp.productId === p.productId)?.isFallback ?? false
          })),
          earliestEffectiveDate: earliestEffectiveDate,
          validTimePeriodForWeighOut: earliestEffectiveDate.isBefore(weighedOutAt) && c.effectiveDate ? moment.utc(c.effectiveDate).isBefore(weighedOutAt) : false
        }
      }))

      let validMappedContracts = mappedContracts.filter(mc => mc.products.length > 0 && mc.validTimePeriodForWeighOut)
      const productId = productProfileId ?? (validMappedContracts.length === 1 && validMappedContracts[0].products.length === 1
        ? validMappedContracts[0].products[0].productId
        : validMappedContracts.length === 1 && validMappedContracts[0].products.length > 1
          ? validMappedContracts[0].products.filter(p => !p.isFallback).length === 1
            ? validMappedContracts[0].products.filter(p => !p.isFallback)[0].productId
            : undefined
          : undefined)

      const product = !isNaN(productId) ? await this.productPromises.get(productId) : undefined
      const isScaledProduct = product?.isScalable ?? false
      const isExpanded = (!ticket[0].hasScaleData ?? false) && isScaledProduct

      if (product && !isScaledProduct && ticket[0].hasScaleData) {
        return this.createPromiseResponse(false, i18n.t('ticketNumberHasScaleDataButProductIsNotScalable', { ticketNumber }), ticketNumber)
      }

      const ticketType = isScaledProduct && isExpanded
        ? ScaleSheetTicketType.Expanded
        : isScaledProduct && !isExpanded
          ? ScaleSheetTicketType.IncludesVolume
          : product
            ? ScaleSheetTicketType.WeightBased
            : undefined

      if (validMappedContracts.length === 0) {
        const invalidContracts = mappedContracts.filter(mc => mc.products.length === 0 || !mc.validTimePeriodForWeighOut)
        if (invalidContracts.length === 1) {
          const contract = invalidContracts[0]
          const errorMessages = []
          if (contract.products.length === 0 && isScaledProduct) {
            errorMessages.push(i18n.t('ticketNumberDoesNotHaveValidProductForSpeciesAndGrades', { ticketNumber }))
          } else if (contract.products.length === 0 && !isScaledProduct) {
            errorMessages.push(i18n.t('ticketNumberDoesNotHaveValidProducts', { ticketNumber }))
          }
          if (weighedOutAt.isBefore(moment.utc(contract.earliestEffectiveDate))) {
            errorMessages.push(i18n.t('ticketNumberWeighOutDateIsBeforeContractsActivityEffectiveDate', { ticketNumber, weighedOutAt: weighedOutAt.local().format('YYYY-MM-DD-THH:mm:ss'), effectiveDate: contract.earliestEffectiveDate.local().format('YYYY-MM-DD-THH:mm:ss') }))
          }
          if (weighedOutAt.isBefore(moment.utc(contract.effectiveDate))) {
            errorMessages.push(i18n.t('ticketNumberWeighOutDateIsBeforeContractEffectiveDate', { ticketNumber, weighedOutAt: weighedOutAt.local().format('YYYY-MM-DD-THH:mm:ss'), effectiveDate: moment.utc(contract.effectiveDate).local().format('YYYY-MM-DD-THH:mm:ss') }))
          }
          return this.createPromiseResponse(false, errorMessages, ticketNumber)
        }
        const invalidProductContractsCount = invalidContracts.filter(ic => ic.products.length === 0).length
        const invalidDateContractsCount = invalidContracts.filter(ic => !ic.validTimePeriodForWeighOut).length
        const errorMessages = []
        if (invalidProductContractsCount > 0 && isScaledProduct) {
          errorMessages.push(i18n.t('contractsWithInvalidProductForSpeciesAndGradesForTicketNumber', { ticketNumber, contracts: invalidProductContractsCount }))
        } else if (invalidProductContractsCount > 0 && !isScaledProduct) {
          errorMessages.push(i18n.t('contractsWithNoValidProductsForTicketNumber', { ticketNumber, contracts: invalidProductContractsCount }))
        }
        if (invalidDateContractsCount > 0) {
          errorMessages.push(i18n.t('contractsWithInvalidTimePeriodForWeighOutForTicketNumber', { ticketNumber, contracts: invalidDateContractsCount }))
        }
        return this.createPromiseResponse(false, errorMessages, ticketNumber)
      }

      if (productProfileId && validMappedContracts.length === 1 && !validMappedContracts[0].products.some(p => p.productId === productProfileId)) {
        return this.createPromiseResponse(false, [i18n.t('ticketNumberDoesNotHaveValidProductOnFoundContract', { ticketNumber })], ticketNumber)
      }

      let alreadyScaled = (validMappedContracts.some(mc => alreadyScaledLogsContractIds.includes(mc.contractId)) ||
        validMappedContracts.some(mc => mc.contractId === alreadyScaledLogYardSaleContractId)) &&
        validMappedContracts.filter(mc => (alreadyScaledLogsContractIds.includes(mc.contractId) || mc.contractId === alreadyScaledLogYardSaleContractId)).length === validMappedContracts.length

      const contractsIncludingAlreadyScaled = validMappedContracts
      validMappedContracts = validMappedContracts.filter(mc =>
        !alreadyScaledLogsContractIds.includes(mc.contractId) &&
        mc.contractId !== alreadyScaledLogYardSaleContractId
      )

      alreadyScaled ||= validMappedContracts.length === 0

      return {
        data: {
          ticket: ticket,
          extTicketNumber2: ticketNumber,
          destination: ticket[0].destination,
          trailerIdentifier: ticket[0].trailerIdentifier ?? this.defaultTrailerIdentifier(),
          contractId: validMappedContracts.length === 1 ? validMappedContracts[0].contractId ?? undefined : undefined,
          tractId: validMappedContracts.length === 1 ? validMappedContracts[0].tractId ?? undefined : undefined,
          productId: productId,
          contracts: validMappedContracts,
          ticketType: ticketType,
          contractType: validMappedContracts.length === 1 ? validMappedContracts[0].type : undefined,
          volumeData: ticket[0].hasScaleData ? this.createVolumeData(volumeInformationForTicket.validSegments, validMappedContracts.length === 1 ? validMappedContracts[0].scalingRegion : 0, 0) : undefined // TODO get right scale rule
        },
        alreadyScaled: alreadyScaled,
        contractsIncludingAlreadyScaled,
        result: true,
        message: i18n.t('ticketNumberContractInformationReadSuccessfully', { ticketNumber }),
        ticketNumber: ticketNumber
      }
    }))
    const contractInformationConstructed = results
      .filter(r => r.result && r.data && !r.alreadyScaled)
      .map(r => r.data)

    this.skippedTicketContracts = results
      .filter(r => r.result && r.data && r.alreadyScaled)
      .map(r => ({ ...r.data, contracts: r.contractsIncludingAlreadyScaled }))

    const groupedByTicketNumber = contractInformationConstructed.reduce((acc, ci) => {
      const key = String(ci.extTicketNumber2)
      if (!acc[key]) acc[key] = []
      acc[key].push(ci)
      return acc
    }, {})

    for (const group of Object.values(groupedByTicketNumber)) {
      if (group.length < 2) continue
      let changed = true
      while (changed) {
        changed = false
        const takenContractIds = new Set(
          group.filter(ci => ci.contractId !== undefined).map(ci => ci.contractId)
        )
        if (takenContractIds.size === 0) break
        for (const ci of group) {
          if (ci.contractId !== undefined) continue
          const remainingContracts = ci.contracts.filter(c => !takenContractIds.has(c.contractId))
          if (remainingContracts.length === ci.contracts.length) continue
          changed = true
          ci.contracts = remainingContracts
          if (remainingContracts.length === 1) {
            ci.contractId = remainingContracts[0].contractId
            ci.tractId = remainingContracts[0].tractId
            ci.contractType = remainingContracts[0].type
          }
        }
      }
    }

    const failedResults = results.filter(r => !r.result)
    if (failedResults.length > 0) {
      this.importErrors = {
        errors: failedResults.map(r => ({ ticketNumber: r.ticketNumber, messages: r.messages }))
      }
      store.dispatch('snackbar/setSnackError', i18n.t('fileImportFailed', { errorCount: failedResults.length }))
      return undefined
    }

    if (contractInformationConstructed.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noImportableTicketsFoundInScaleData'))
      return undefined
    }

    return contractInformationConstructed.map((ci, index) => {
      return { ...ci, index: index }
    })
  }

  async constructTicketsFromScaleSheet (tickets) {
    const results = await Promise.all(tickets.map(async t => {
      const ticketNumber = t.ticketNumber
      const numericTicketNumber = Number(ticketNumber)
      const ticket = t.ticketContent
      if (isNaN(numericTicketNumber)) {
        return this.createPromiseResponse(false, [i18n.t('ticketNumberIsNotNumeric', { ticketNumber })], ticketNumber, undefined, [])
      }
      if (ticket[0].contractType === ContractType.LogYardSale.value) {
        const ticketConstructed = await this.getLogYardSaleTicketData(numericTicketNumber)
        if (!this.contractPromises.has(ticketConstructed.contractId)) {
          this.contractPromises.set(ticketConstructed.contractId, store.dispatch('contract/getContract', ticketConstructed.contractId))
        }
        const contract = await this.contractPromises.get(ticketConstructed.contractId)
        const volumeData = ticket[0].ticketType === ScaleSheetTicketType.IncludesVolume ? await this.generateSegmentVolumes(ticket) : {}
        const invalidSegments = ticket[0].ticketType === ScaleSheetTicketType.IncludesVolume ? volumeData.invalidSegments : []
        const validSegments = ticket[0].ticketType === ScaleSheetTicketType.IncludesVolume ? volumeData.validSegments : []
        const productId = ticketConstructed.productId ?? ticketConstructed.productID
        const result = invalidSegments.length === 0
        return this.createPromiseResponse(
          result,
          result ? [''] : [i18n.t('invalidLogSegmentInformation', { ticketNumber })],
          ticketNumber,
          {
            ticket: ticketConstructed,
            volumeData: ticket[0].ticketType === ScaleSheetTicketType.IncludesVolume ? this.createVolumeData(validSegments, contract.scalingRegion, 4) : {}, // TODO get right scale rule
            destination: contract.destination,
            account: contract.account,
            fromAccount: contract.fromAccount,
            contractId: contract.contractId,
            product: contract.products.find(p => p.productId === productId)?.product ?? contract.products.find(p => p.productId === productId)?.name ?? '',
            fileName: this.scaleSheetFile.name.split('.').slice(0, -1).join(''),
            alreadyCreated: true,
            ticketType: ticket[0].ticketType,
            contractType: ticket[0].contractType
          },
          result ? [] : invalidSegments
        )
      } else {
        const tractId = ticket[0].tractId
        if (tractId === undefined) {
          return this.createPromiseResponse(false, [i18n.t('ticketNumberIsNotInTicketBook', { ticketNumber })], ticketNumber, undefined, [])
        }

        if (tractId === 0) {
          return this.createPromiseResponse(false, [i18n.t('ticketNumberIsNotOnValidTract', { ticketNumber })], ticketNumber, undefined, [])
        }

        if (!this.tractPromises.has(tractId)) {
          this.tractPromises.set(tractId, store.dispatch('tract/fetchTract', tractId))
        }

        const tract = await this.tractPromises.get(tractId)

        if (tract === undefined) {
          return this.createPromiseResponse(false, [i18n.t('ticketNumberIsNotOnValidTract', { ticketNumber })], ticketNumber, undefined, [])
        }

        if (!this.contractsOnTractPromises.has(tractId)) {
          this.contractsOnTractPromises.set(tractId, store.dispatch('tract/fetchContractsForTract', tractId))
        }

        const contractsOnTract = await this.contractsOnTractPromises.get(tractId)
        this.contractsForTracts.set(tractId, contractsOnTract)

        const contract = this.contractsForTracts.get(tractId).find(c => c.contractId === ticket[0].contractId)

        if (contract === undefined) {
          return this.createPromiseResponse(false, [i18n.t('ticketNumberDoesNotHaveValidOpenContract', { ticketNumber })], ticketNumber, undefined, [])
        }

        const ticketAlreadyCreatedId = await this.ticketBooks
          .filter(tb => tb.tractId === tractId)
          .find(tb => tb.firstPage <= numericTicketNumber && tb.lastPage >= numericTicketNumber)
          ?.pages.find(p => p.pageNumber === numericTicketNumber)?.ticketId

        const ticketConstructed = ticketAlreadyCreatedId ? await this.getTicketData(ticketAlreadyCreatedId) : this.createTicketData(ticket, tract, contract)
        if (ticketConstructed === undefined) {
          const errorMessage = ticketAlreadyCreatedId ? i18n.t('ticketNumberCouldNotBeRetrieved', { ticketNumber }) : i18n.t('ticketNumberWeightsCouldNotBeCalculated', { ticketNumber })
          return this.createPromiseResponse(false, [errorMessage], ticketNumber, undefined, undefined)
        }

        if (ticketAlreadyCreatedId) {
          if (ticketConstructed.scalingStatus !== ScalingStatus.Ready.value) {
            return this.createPromiseResponse(false, [i18n.t('ticketNumberIsNotReadyForScaling', { ticketNumber })], ticketNumber, undefined, [])
          }
          const contractKeys = {
            contractId: ticketConstructed.contractId,
            tractId: ticketConstructed.tractId,
            contractAccountId: ticketConstructed.accountId,
            destinationAccountId: ticketConstructed.destinationAccountId,
            settingId: ticketConstructed.settingId,
            productId: ticketConstructed.productID
          }
          const isValidContract = await this.validateContractInformationForSingleTicket(ticket, contractKeys)
          if (!isValidContract) {
            return this.createPromiseResponse(false, i18n.t('errorValidatingContractOn', { ticketNumber }), ticketNumber, undefined, [])
          }
        }

        const volumeData = ticket[0].ticketType === ScaleSheetTicketType.IncludesVolume ? await this.generateSegmentVolumes(ticket) : {}
        const invalidSegments = ticket[0].ticketType === ScaleSheetTicketType.IncludesVolume ? volumeData.invalidSegments : []
        const validSegments = ticket[0].ticketType === ScaleSheetTicketType.IncludesVolume ? volumeData.validSegments : []
        const productId = ticketConstructed.productId ?? ticketConstructed.productID
        const result = invalidSegments.length === 0
        return this.createPromiseResponse(
          result,
          result ? [''] : [i18n.t('invalidLogSegmentInformation', { ticketNumber })],
          ticketNumber,
          {
            ticket: ticketConstructed,
            volumeData: ticket[0].ticketType === ScaleSheetTicketType.IncludesVolume ? this.createVolumeData(validSegments, tract.scalingRegion, 4) : {}, // TODO get right scale rule
            tract: contract.tract,
            destination: contract.destination,
            account: contract.account,
            setting: contract.setting,
            contractId: contract.contractId,
            product: contract.products.find(p => p.productId === productId)?.product ?? contract.products.find(p => p.productId === productId)?.name ?? '',
            fileName: this.scaleSheetFile.name.split('.').slice(0, -1).join(''),
            alreadyCreated: ticketAlreadyCreatedId !== undefined && ticketAlreadyCreatedId !== null,
            ticketType: ticket[0].ticketType,
            contractType: ticket[0].contractType
          },
          result ? [] : invalidSegments
        )
      }
    }))
    const ticketsConstructed = results.filter(r => r.result).map(r => r.ticketData)
    if (results.filter(r => !r.result).length > 0) {
      const failedResults = results.filter(r => !r.result)
      this.importErrors = {
        errors: failedResults.map(r => ({ ticketNumber: r.ticketNumber, messages: r.messages }))
      }
      store.dispatch('snackbar/setSnackError', i18n.t('fileImportFailed', { errorCount: failedResults.length }))
      return undefined
    }
    return ticketsConstructed
  }

  batchNumbersByUrlLength (numbers, maxUrlLength) {
    const FIXED_QUERY_OVERHEAD = 'includeInUse=true&includeArchived=false&includeAvailable=false&pageNumbers='.length
    const BATCH_OVERHEAD = 1
    const batches = []
    let currentBatch = []
    numbers.forEach(num => {
      const testBatch = [...currentBatch, num]
      const pageNumbersParam = testBatch.join(',')
      const totalLength = BATCH_OVERHEAD + FIXED_QUERY_OVERHEAD + pageNumbersParam.length
      if (totalLength > maxUrlLength && currentBatch.length > 0) {
        batches.push(currentBatch)
        currentBatch = [num]
      } else {
        currentBatch.push(num)
      }
    })
    if (currentBatch.length > 0) {
      batches.push(currentBatch)
    }
    return batches
  }

  createPromiseResponse (result, messages, ticketNumber, ticketData, invalidSegments) {
    return {
      result: result,
      messages: messages,
      ticketNumber: ticketNumber,
      ticketData: ticketData,
      invalidSegments: invalidSegments
    }
  }

  createVolumeData (segmentVolumes, region, scaleRule) {
    return {
      ticketId: 0,
      region,
      scaleRule,
      segmentVolumes
    }
  }

  createVolumeDataForSingleTicket (ticketId, segmentVolumes, region, scaleRule) {
    return {
      ...this.createVolumeData(segmentVolumes, region, scaleRule),
      ticketId
    }
  }

  createTicketData (ticketInformation, tract, contract) {
    const timeZoneOffset = store.getters['user/companyInfo'].timeZoneOffset
    const offsetInMinutes = timeZoneOffset * 60
    const inWeight = this.getInWeightFromTicket(ticketInformation[0])
    if (inWeight <= 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('ticketNumberDoesNotHaveValidWeight', { ticketNumber: ticketInformation.ticketNumber, weightType: i18n.t('inWeight') }))
      return undefined
    }
    const outWeight = this.getOutWeightFromTicket(ticketInformation[0], inWeight)
    if (outWeight <= 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('ticketNumberDoesNotHaveValidWeight', { ticketNumber: ticketInformation.ticketNumber, weightType: i18n.t('outWeight') }))
      return undefined
    }
    if (!isNaN(ticketInformation[0].netWeight)) {
      const netWeight = this.getNetWeightFromTicket(ticketInformation[0], inWeight, outWeight)
      if (netWeight <= 0 || netWeight !== (inWeight - outWeight)) {
        store.dispatch('snackbar/setSnackError', i18n.t('ticketNumberDoesNotHaveValidWeight', { ticketNumber: ticketInformation.ticketNumber, weightType: i18n.t('netWeight') }))
        return undefined
      }
    }
    const now = moment.utc()
    const hasDateValue = ticketInformation[0].weighedOutAt !== undefined && ticketInformation[0].weighedOutAt !== null && ticketInformation[0].weighedOutAt !== ''
    const weighedOutAtAnchored = hasDateValue
      ? moment.utc(ticketInformation[0].weighedOutAt).utcOffset(offsetInMinutes, true).set({ hour: 8, minute: 0, second: 0, millisecond: 0 }).utc()
      : null
    const weighedOutAt = hasDateValue
      ? (weighedOutAtAnchored.isAfter(now) ? now.clone().format() : weighedOutAtAnchored.format())
      : undefined
    const weighedInAtAnchored = hasDateValue
      ? moment.utc(ticketInformation[0].weighedOutAt).utcOffset(offsetInMinutes, true).set({ hour: 7, minute: 55, second: 0, millisecond: 0 }).utc()
      : null
    const weighedInAt = weighedOutAt
      ? (weighedInAtAnchored.isAfter(now) ? now.clone().subtract(5, 'minutes').format() : weighedInAtAnchored.format())
      : undefined
    const loadCreatedAtAnchored = hasDateValue
      ? moment.utc(ticketInformation[0].weighedOutAt).utcOffset(offsetInMinutes, true).set({ hour: 7, minute: 50, second: 0, millisecond: 0 }).utc()
      : null
    const loadCreatedAt = weighedInAt
      ? (loadCreatedAtAnchored.isAfter(now) ? now.clone().subtract(10, 'minutes').format() : loadCreatedAtAnchored.format())
      : undefined
    const ticketRequestObject = {
      ticketId: null,
      originationTicketId: getOriginationTicketId(contract.contractId),
      trailerIdentifier: ticketInformation[0].trailerIdentifier?.trim()?.substring(0, NAME_TRAILER_IDENTIFIER_INDEX)?.trim() ?? this.defaultTrailerIdentifier(),
      seal: null,
      latitude: tract.spot.latitude,
      longitude: tract.spot.longitude,
      productId: ticketInformation[0].productId ?? null,
      contractId: contract.contractId,
      loggerAccountId: contract.requiresLogger ? tract.loggers.find(l => l.isDefault)?.accountId ?? tract.loggers[0]?.accountId : null,
      extTicketNumber1: '',
      extTicketNumber2: ticketInformation[0].ticketNumber,
      inWeight: inWeight,
      outWeight: outWeight,
      defectWeight: 0,
      departureWeight: 0,
      departedAt: null,
      pieces: 0,
      driverAccountId: null,
      loadCreatedAt: loadCreatedAt ?? moment.utc().format(),
      weighedInAt: weighedInAt ?? moment.utc().format(),
      weighedOutAt: weighedOutAt ?? moment.utc().format(),
      averageLength: 0,
      isByProducts: false,
      rolloutStatus: null,
      yardInstructionId: null,
      vehicleType: VehicleType.Trailer.value,
      scalingStatus: ticketInformation[0].ticketType.details.scalingStatus
    }
    return ticketRequestObject
  }

  async getTicketData (ticketId) {
    if (!this.ticketPromises.has(ticketId)) {
      this.ticketPromises.set(ticketId, store.dispatch('ticket/fetchTicketById', ticketId))
    }
    const ticket = await this.ticketPromises.get(ticketId)
    return ticket
  }

  async getLogYardSaleTicketData (ticketNumber) {
    if (!this.ticketPromises.has(ALL_LOG_YARD_SALE_TICKETS_FETCH_KEY)) {
      this.ticketPromises.set(ALL_LOG_YARD_SALE_TICKETS_FETCH_KEY, store.dispatch('ticket/fetchLogYardSaleTicketsForScaleDataImport'))
    }
    return (await this.ticketPromises.get(ALL_LOG_YARD_SALE_TICKETS_FETCH_KEY))?.find(t => t.ticketNumber === ticketNumber && t.contractType === ContractType.LogYardSale.value)
  }

  getInWeightFromTicket (ticketInformation) {
    const netWeight = this.parseWeight(ticketInformation.netWeight)
    const inWeight = this.parseWeight(ticketInformation.inWeight)
    if (!isNaN(inWeight)) {
      return inWeight
    } else if (!isNaN(netWeight) && this.profile.defaultOutWeight !== null && this.profile.defaultOutWeight !== undefined) {
      return netWeight + this.profile.defaultOutWeight
    } else if (this.profile.defaultInWeight !== undefined) {
      return this.profile.defaultInWeight
    }
    return 0
  }

  getOutWeightFromTicket (ticketInformation, inWeight) {
    const netWeight = this.parseWeight(ticketInformation.netWeight)
    const outWeight = this.parseWeight(ticketInformation.outWeight)
    if (!isNaN(outWeight)) {
      return outWeight
    } else if (!isNaN(netWeight) && inWeight != null) {
      return inWeight - netWeight
    } else if (this.profile.defaultOutWeight !== undefined) {
      return this.profile.defaultOutWeight
    }
    return 0
  }

  getNetWeightFromTicket (ticketInformation, inWeight, outWeight) {
    const netWeight = this.parseWeight(ticketInformation.netWeight)
    if (!isNaN(netWeight)) return netWeight
    if (inWeight > 0 && outWeight > 0) return inWeight - outWeight
    return 0
  }

  parseWeight (value) {
    return (value !== null && value !== undefined && String(value).trim().length > 0)
      ? Number(String(value).replace(',', ''))
      : NaN
  }

  async generateSegmentVolumes (parsedData) {
    const logSegments = []
    const invalidSegments = []
    await Promise.all(parsedData.map(async item => {
      const speciesId = this.findSpeciesId(item.species)
      const grossVolumeSegmentOneProvided = item.segmentOneGrossVolume !== null &&
        item.segmentOneGrossVolume !== undefined &&
        item.segmentOneGrossVolume !== '' &&
        !isNaN(item.segmentOneGrossVolume)
      const netVolumeSegmentOneProvided = item.segmentOneNetVolume !== null &&
        item.segmentOneNetVolume !== undefined &&
        item.segmentOneNetVolume !== '' &&
        !isNaN(item.segmentOneNetVolume)
      const defectVolumeSegmentOneProvided = item.segmentOneDefectVolume !== null &&
        item.segmentOneDefectVolume !== undefined &&
        item.segmentOneDefectVolume !== '' &&
        !isNaN(item.segmentOneDefectVolume)
      const grossVolumeSegmentOne = grossVolumeSegmentOneProvided
        ? Number(item.segmentOneGrossVolume)
        : defectVolumeSegmentOneProvided
          ? Number(item.segmentOneNetVolume) + Number(item.segmentOneDefectVolume)
          : Number(item.segmentOneNetVolume)
      const netVolumeSegmentOne = netVolumeSegmentOneProvided
        ? Number(item.segmentOneNetVolume)
        : grossVolumeSegmentOneProvided && defectVolumeSegmentOneProvided
          ? Number(grossVolumeSegmentOne) - Number(item.segmentOneDefectVolume)
          : Number(grossVolumeSegmentOne)
      const defectVolumeSegmentOne = defectVolumeSegmentOneProvided ? Number(item.segmentOneDefectVolume) : grossVolumeSegmentOne - netVolumeSegmentOne
      const segmentOne = {
        logNumber: Number(item.logNumber),
        segmentNumber: 1,
        diameter: item.segmentOneDiameter,
        length: item.segmentOneLength,
        speciesId: speciesId,
        gradeId: this.findGradeId(item.segmentOneGrade),
        endUseSortId: this.findEndUseSortId(item.segmentOneEndUseSort),
        defectDefinitionId: null,
        lengthCut: 0,
        diameterCut: 0,
        volumeCut: defectVolumeSegmentOne,
        buttLog: this.getButtLogStatus(item.buttLog),
        grossVolume: this.calculateVolumeWithFactor(grossVolumeSegmentOne),
        netVolume: this.calculateVolumeWithFactor(netVolumeSegmentOne)
      }
      const validStatus = this.logSegmentOneIsValid(segmentOne)
      if (validStatus.status) {
        logSegments.push(segmentOne)
      } else {
        invalidSegments.push(validStatus)
      }
      let logHasAdditionalSegments = false
      if (this.hasSegmentTwoLength(item)) {
        logHasAdditionalSegments = true
        const grossVolumeSegmentTwoProvided = item.segmentTwoGrossVolume !== null &&
          item.segmentTwoGrossVolume !== undefined &&
          item.segmentTwoGrossVolume !== '' &&
          !isNaN(item.segmentTwoGrossVolume)
        const netVolumeSegmentTwoProvided = item.segmentTwoNetVolume !== null &&
          item.segmentTwoNetVolume !== undefined &&
          item.segmentTwoNetVolume !== '' &&
          !isNaN(item.segmentTwoNetVolume)
        const defectVolumeSegmentTwoProvided = item.segmentTwoDefectVolume !== null &&
          item.segmentTwoDefectVolume !== undefined &&
          item.segmentTwoDefectVolume !== '' &&
          !isNaN(item.segmentTwoDefectVolume)
        const grossVolumeSegmentTwo = grossVolumeSegmentTwoProvided
          ? Number(item.segmentTwoGrossVolume)
          : defectVolumeSegmentTwoProvided
            ? Number(item.segmentTwoNetVolume) + Number(item.segmentTwoDefectVolume)
            : Number(item.segmentTwoNetVolume)
        const netVolumeSegmentTwo = netVolumeSegmentTwoProvided
          ? Number(item.segmentTwoNetVolume)
          : grossVolumeSegmentTwoProvided && defectVolumeSegmentTwoProvided
            ? Number(grossVolumeSegmentTwo) - Number(item.segmentTwoDefectVolume)
            : Number(grossVolumeSegmentTwo)
        const defectVolumeSegmentTwo = defectVolumeSegmentTwoProvided ? Number(item.segmentTwoDefectVolume) : grossVolumeSegmentTwo - netVolumeSegmentTwo
        const gradeIdSegmentTwo = item.segmentTwoGrade !== null && item.segmentTwoGrade !== undefined && item.segmentTwoGrade !== ''
          ? this.findGradeId(item.segmentTwoGrade)
          : this.findGradeId(item.segmentOneGrade)
        const endUseSortIdSegmentTwo = item.segmentTwoEndUseSort !== null && item.segmentTwoEndUseSort !== undefined && item.segmentTwoEndUseSort !== ''
          ? this.findEndUseSortId(item.segmentTwoEndUseSort)
          : this.findEndUseSortId(item.segmentOneEndUseSort)
        const segmentTwo = {
          logNumber: Number(item.logNumber),
          segmentNumber: 2,
          diameter: item.segmentTwoDiameter,
          length: item.segmentTwoLength,
          speciesId: speciesId,
          gradeId: gradeIdSegmentTwo,
          endUseSortId: endUseSortIdSegmentTwo,
          defectDefinitionId: null,
          lengthCut: 0,
          diameterCut: 0,
          volumeCut: defectVolumeSegmentTwo,
          buttLog: this.getButtLogStatus(item.buttLog),
          grossVolume: this.calculateVolumeWithFactor(grossVolumeSegmentTwo),
          netVolume: this.calculateVolumeWithFactor(netVolumeSegmentTwo)
        }
        const validStatus = this.logSegmentTwoIsValid(segmentTwo, segmentOne)
        if (validStatus.status) {
          logSegments.push(segmentTwo)
        } else {
          invalidSegments.push(validStatus)
        }
      }
      if (item.segmentThreeLength !== 0 && item.segmentThreeLength !== null && item.segmentThreeLength !== '' && item.segmentThreeLength !== '0' && item.segmentThreeLength !== undefined && !isNaN(item.segmentThreeLength)) {
        logHasAdditionalSegments = true
        const grossVolumeSegmentThreeProvided = item.segmentThreeGrossVolume !== null &&
          item.segmentThreeGrossVolume !== undefined &&
          item.segmentThreeGrossVolume !== '' &&
          !isNaN(item.segmentThreeGrossVolume)
        const netVolumeSegmentThreeProvided = item.segmentThreeNetVolume !== null &&
          item.segmentThreeNetVolume !== undefined &&
          item.segmentThreeNetVolume !== '' &&
          !isNaN(item.segmentThreeNetVolume)
        const grossVolumeSegmentThree = grossVolumeSegmentThreeProvided
          ? Number(item.segmentThreeGrossVolume)
          : Number(item.segmentThreeNetVolume)
        const netVolumeSegmentThree = netVolumeSegmentThreeProvided
          ? Number(item.segmentThreeNetVolume)
          : Number(grossVolumeSegmentThree)
        const segmentThree = {
          logNumber: Number(item.logNumber),
          segmentNumber: 3,
          diameter: item.segmentThreeDiameter,
          length: item.segmentThreeLength,
          speciesId: speciesId,
          gradeId: this.findGradeId(item.segmentOneGrade),
          endUseSortId: this.findEndUseSortId(item.segmentOneEndUseSort),
          defectDefinitionId: null,
          lengthCut: 0,
          diameterCut: 0,
          volumeCut: 0,
          buttLog: this.getButtLogStatus(item.buttLog),
          grossVolume: this.calculateVolumeWithFactor(grossVolumeSegmentThree),
          netVolume: this.calculateVolumeWithFactor(netVolumeSegmentThree)
        }
        const validStatus = {
          segment: segmentThree,
          status: false,
          message: i18n.t('canNotAcceptMoreThanTwoSegments', { logNumber: segmentThree.logNumber })
        }
        invalidSegments.push(validStatus)
      }
      // A single segment log can not be marked as a butt log (server rejects with SSNB1).
      if (validStatus.status && !logHasAdditionalSegments && segmentOne.buttLog) {
        invalidSegments.push({
          segment: segmentOne,
          status: false,
          message: i18n.t('singleSegmentCanNotBeButtLog', { logNumber: segmentOne.logNumber })
        })
      }
    }))
    return {
      validSegments: logSegments,
      invalidSegments: invalidSegments
    }
  }

  logSegmentOneIsValid (segment) {
    if (segment.logNumber > MAX_NUMBER_OF_LOGS_PER_TICKET) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('logNumberExceedsMax', { logNumber: segment.logNumber, maxLogNumber: MAX_NUMBER_OF_LOGS_PER_TICKET, segmentNumber: segment.segmentNumber })
      }
    }
    if (!segment.speciesId) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('invalidSpecies', { logNumber: segment.logNumber, segmentNumber: segment.segmentNumber })
      }
    }
    if (!segment.gradeId) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('invalidGrade', { logNumber: segment.logNumber, segmentNumber: segment.segmentNumber })
      }
    }
    if (isNaN(segment.length) || segment.length < LENGTH_LOWER_BOUND || segment.length > LENGTH_UPPER_BOUND) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('segmentLengthMustBeInRange', { logNumber: segment.logNumber, segmentNumber: segment.segmentNumber, lengthLowerBound: LENGTH_LOWER_BOUND, lengthUpperBound: LENGTH_UPPER_BOUND })
      }
    }
    if (isNaN(segment.diameter) || segment.diameter < DIAMETER_LOWER_BOUND || segment.diameter > DIAMETER_UPPER_BOUND) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('segmentDiameterMustBeInRange', { logNumber: segment.logNumber, segmentNumber: segment.segmentNumber, diameterLowerBound: DIAMETER_LOWER_BOUND, diameterUpperBound: DIAMETER_UPPER_BOUND })
      }
    }
    if (segment.grossVolume < 0 || isNaN(segment.grossVolume)) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('segmentGrossVolumeCanNotBeNegative', { logNumber: segment.logNumber, segmentNumber: segment.segmentNumber })
      }
    }
    if (segment.grossVolume > VOLUME_UPPER_BOUND) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('segmentGrossVolumeExceedsUpperBound', { logNumber: segment.logNumber, segmentNumber: segment.segmentNumber, volumeUpperBound: VOLUME_UPPER_BOUND })
      }
    }
    if (segment.netVolume < 0 || isNaN(segment.netVolume)) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('segmentNetVolumeCanNotBeNegative', { logNumber: segment.logNumber, segmentNumber: segment.segmentNumber })
      }
    }
    if (segment.netVolume > VOLUME_UPPER_BOUND) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('segmentNetVolumeExceedsUpperBound', { logNumber: segment.logNumber, segmentNumber: segment.segmentNumber, volumeUpperBound: VOLUME_UPPER_BOUND })
      }
    }
    return {
      segment: segment,
      status: true,
      message: i18n.t('validSegment')
    }
  }

  logSegmentTwoIsValid (segment, previousSegment) {
    if (!this.logSegmentOneIsValid(segment).status) {
      return this.logSegmentOneIsValid(segment)
    }
    if (segment.diameter < previousSegment.diameter) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('segmentTwoDiameterSmallerThanSegmentOne', { logNumber: segment.logNumber })
      }
    }
    if (segment && !previousSegment) {
      return {
        segment: segment,
        status: false,
        message: i18n.t('canNotHaveSegmentTwoWithoutValidSegmentOne', { logNumber: segment.logNumber })
      }
    }
    return {
      segment: segment,
      status: true,
      message: i18n.t('validSegment')
    }
  }

  findSpeciesId (code) {
    return this.profile.speciesProfiles.find(sp => sp.externalSpeciesCode === code)?.speciesId
  }

  findSpeciesName (id) {
    return this.profile.speciesProfiles.find(sp => sp.speciesId === id)?.externalSpeciesCode
  }

  findGradeId (code) {
    return this.profile.gradeProfiles.find(grade => grade.externalGradeCode === code)?.gradeId ?? this.profile.defaultGradeId
  }

  findGradeName (id) {
    return this.profile.gradeProfiles.find(grade => grade.gradeId === id)?.externalGradeCode
  }

  findEndUseSortId (code) {
    return this.profile.endUseSortProfiles.find(eus => eus.externalEndUseSortCode === code)?.endUseSortId
  }

  findEndUseSortName (id) {
    return this.profile.endUseSortProfiles.find(eus => eus.endUseSortId === id)?.externalEndUseSortCode
  }

  async findContractInformation (ticket) {
    const { ticketNumber } = ticket

    const getMatchingTicketBooks = (ticketBooks) =>
      ticketBooks.filter(tb => tb.firstPage <= ticketNumber && tb.lastPage >= ticketNumber)

    const matchingTicketBooks = getMatchingTicketBooks(this.ticketBooks)

    const fetchContractsForTract = async (tractId) => {
      if (!this.contractsOnTractPromises.has(tractId)) {
        this.contractsOnTractPromises.set(tractId, store.dispatch('tract/fetchContractsForTract', tractId))
      }

      const contractsOnTract = await this.contractsOnTractPromises.get(tractId)
      this.contractsForTracts.set(tractId, contractsOnTract)
      return contractsOnTract
    }

    const tractId = this.profile.tractProfiles.find(tp => tp.externalTractName === ticket.tract)?.tractId
    const contractAccountId = this.profile.accountProfiles.find(ap => ap.externalAccountName === ticket.contractAccount)?.accountId
    const destinationAccountId = this.profile.accountProfiles.find(ap => ap.externalAccountName === ticket.destination)?.accountId
    const settingId = this.profile.settingProfiles.find(sp => sp.externalSettingName === ticket.setting)?.settingId ?? this.profile.defaultSettingId
    const productId = this.profile.productProfiles.find(pp => pp.externalProductName === ticket.product)?.productId

    if (tractId) {
      if (!matchingTicketBooks.some(tb => tb.tractId === tractId)) {
        return this.findContractResponse(undefined, undefined, undefined, ticketNumber,
          i18n.t('ticketNumberIsNotOnValidTract', { ticketNumber }) + ' TCFT0')
      }
      const contractsOnTract = await fetchContractsForTract(tractId)
      const matchingContracts = this.filterContracts(contractsOnTract, { contractAccountId, destinationAccountId, settingId, tractId, productId })
      if (matchingContracts.length === 1) {
        return this.findContractResponse(matchingContracts[0].contractId, tractId, matchingContracts, ticketNumber, undefined)
      } else if (matchingContracts.length > 1) {
        return this.findContractResponse(undefined, tractId, matchingContracts, ticketNumber, undefined)
      } else {
        return this.findContractResponse(undefined, undefined, undefined, ticketNumber,
          i18n.t('ticketNumberDoesNotHaveValidOpenContract', { ticketNumber }) + ' TCFT1')
      }
    }

    // Fallback to searching through all ticket books and log yard sale tickets
    if (!this.ticketPromises.has(ALL_LOG_YARD_SALE_TICKETS_FETCH_KEY)) {
      this.ticketPromises.set(ALL_LOG_YARD_SALE_TICKETS_FETCH_KEY, store.dispatch('ticket/fetchLogYardSaleTicketsForScaleDataImport'))
    }
    const logYardSaleTickets = await this.ticketPromises.get(ALL_LOG_YARD_SALE_TICKETS_FETCH_KEY)
    const existingTicketContractId = logYardSaleTickets.find(t => t.ticketNumber === Number(ticketNumber))?.contractId
    if (existingTicketContractId && !this.contractPromises.has(existingTicketContractId)) {
      this.contractPromises.set(existingTicketContractId, store.dispatch('contract/getContract', existingTicketContractId))
    }
    const existingTicketContract = existingTicketContractId ? await this.contractPromises.get(existingTicketContractId) : undefined
    const ticketIsLogYardSale = existingTicketContract?.type === ContractType.LogYardSale.value

    const possibleTracts = [...new Set(matchingTicketBooks.map(tb => tb.tractId))]
    if (possibleTracts.length === 0 && !ticketIsLogYardSale) {
      return this.findContractResponse(undefined, undefined, undefined, ticketNumber,
        i18n.t('ticketNumberIsNotOnValidTract', { ticketNumber }) + ' TCFT2')
    }
    if (possibleTracts.length === 1) {
      const tractId = possibleTracts[0]
      const contractsOnTract = await fetchContractsForTract(tractId)
      const matchingContracts = this.filterContracts(contractsOnTract, { contractAccountId, destinationAccountId, settingId, tractId, productId })
      if (ticketIsLogYardSale) {
        matchingContracts.push(existingTicketContract)
      }
      if (matchingContracts.length === 1) {
        return this.findContractResponse(matchingContracts[0].contractId, tractId, matchingContracts, ticketNumber, undefined)
      } else if (matchingContracts.length > 1) {
        return this.findContractResponse(undefined, tractId, matchingContracts, ticketNumber, undefined)
      } else {
        return this.findContractResponse(undefined, undefined, undefined, ticketNumber,
          i18n.t('ticketNumberDoesNotHaveValidOpenContract', { ticketNumber }) + ' TCFT3')
      }
    }

    const tractResults = await Promise.all(possibleTracts.map(async tractId => {
      const contractsOnTract = await fetchContractsForTract(tractId)
      const matchingContracts = this.filterContracts(contractsOnTract, { contractAccountId, destinationAccountId, settingId, tractId, productId })
      return { tractId, matchingContracts }
    }))

    const matchingContractsOverall = this.removeDuplicateContracts(tractResults.flatMap(tr => tr.matchingContracts))

    if (ticketIsLogYardSale) {
      matchingContractsOverall.push(existingTicketContract)
    }

    if (matchingContractsOverall.length === 1) {
      return this.findContractResponse(matchingContractsOverall[0].contractId, matchingContractsOverall[0].tractId, matchingContractsOverall, ticketNumber, undefined)
    } else if (matchingContractsOverall.length > 1) {
      return this.findContractResponse(undefined, undefined, matchingContractsOverall, ticketNumber, undefined)
    } else {
      return this.findContractResponse(undefined, undefined, undefined, ticketNumber,
        i18n.t('ticketNumberDoesNotHaveValidOpenContract', { ticketNumber }) + ' TCFT4')
    }
  }

  // Helper method to get contracts for a single tract
  async getContractsForTract (tractId) {
    if (!this.contractsOnTractPromises.has(tractId)) {
      this.contractsOnTractPromises.set(tractId, store.dispatch('tract/fetchContractsForTract', tractId))
    }
    const contracts = await this.contractsOnTractPromises.get(tractId)
    this.contractsForTracts.set(tractId, contracts)
    return contracts
  }

  // Generic detection of external values that have multiple potential contracts and
  // are not yet mapped on the import profile (i.e. force 'Complete Contract Info' dialog)
  findUnmappedProfileMappings (parsedContractData, category) {
    const mappedNames = new Set(this.profile[category.profileArrayKey].map(m => m[category.externalNameField]))
    const isUnresolved = category.isUnresolved ?? (item => item[category.idField] === undefined)
    const unresolvedItems = new Set(parsedContractData.filter(isUnresolved))

    const carriersByName = new Map()
    for (const item of [...parsedContractData, ...this.skippedTicketContracts]) {
      const fieldsByName = new Map()
      for (const row of item.ticket ?? []) {
        for (const field of category.rowValueFields) {
          const externalName = row[field]
          if (externalName == null || String(externalName).trim() === '') continue
          if (mappedNames.has(externalName)) continue
          if (!fieldsByName.has(externalName)) fieldsByName.set(externalName, new Set())
          fieldsByName.get(externalName).add(field)
        }
      }
      for (const [externalName, fields] of fieldsByName) {
        if (!carriersByName.has(externalName)) carriersByName.set(externalName, [])
        carriersByName.get(externalName).push({ item, fields: [...fields] })
      }
    }

    return [...carriersByName.entries()]
      .map(([externalName, carriers]) => {
        const unresolvedCarriers = carriers.filter(({ item }) => unresolvedItems.has(item))
        const candidates = uniqBy(
          unresolvedCarriers.flatMap(({ item, fields }) => fields.flatMap(field => category.getCandidates([item], field))),
          'value')
        return {
          externalName,
          candidateOptions: candidates.map(option => ({
            ...option,
            invalidTickets: this.findTicketsInvalidForMappingOption(carriers, category, option.value)
          }))
        }
      })
      .filter(r => r.candidateOptions.length > 0)
  }

  // A ticket breaks when none of its contracts satisfies the option in every column it carries the
  // external value in, since all mapped filters apply together when contracts are looked up again.
  // Log yard sale contracts are re-added after account/setting filtering, so they always survive those mappings.
  findTicketsInvalidForMappingOption (carriers, category, optionValue) {
    const invalidTickets = carriers
      .filter(({ item, fields }) => !(item.contracts ?? []).some(contract =>
        (category.logYardSaleContractsIgnoreMapping && contract.type === ContractType.LogYardSale.value) ||
        fields.every(field => category.getCandidates([{ contracts: [contract] }], field).some(o => o.value === optionValue))))
      .map(({ item }) => ({
        ticketNumber: item.extTicketNumber2,
        issueKey: category.getTicketIssueKey?.({ ticketNumber: Number(item.extTicketNumber2), optionValue, ticketBooks: this.ticketBooks }) ?? category.issueKey
      }))
    return uniqBy(invalidTickets, 'ticketNumber')
  }

  findUnmappedScaleDataCodes (category) {
    const mappedCodes = new Set(this.profile[category.profileArrayKey].map(m => m[category.externalNameField]))
    const externalCodes = new Set((this.rawTickets ?? [])
      .filter(t => t.ticketContent[0]?.hasScaleData)
      .flatMap(t => t.ticketContent.flatMap(row => category.rowValueFields
        .filter(field => field !== 'segmentTwoGrade' || this.hasSegmentTwoLength(row))
        .map(field => row[field])))
      .filter(code => code != null && String(code).trim() !== '' && !mappedCodes.has(code)))

    const candidateOptions = category.getCandidates()
    return [...externalCodes]
      .map(externalName => ({ externalName, candidateOptions }))
      .filter(r => r.candidateOptions.length > 0)
  }

  hasSegmentTwoLength (row) {
    return row.segmentTwoLength !== 0 && row.segmentTwoLength !== null && row.segmentTwoLength !== '' && row.segmentTwoLength !== '0' && row.segmentTwoLength !== undefined && !isNaN(row.segmentTwoLength)
  }

  appendCreatedMappings (category, created) {
    created.forEach(m => this.profile[category.profileArrayKey].push(m))
  }

  filterContracts (contracts, { contractAccountId, destinationAccountId, settingId, tractId, productId }) {
    return contracts.filter(contract => {
      const matchesDestination = !destinationAccountId || contract.destinationAccountId === destinationAccountId
      const matchesStatus = contract.status === ContractStatus.Open.value
      const matchesSetting = !settingId || contract.settingId === settingId
      const matchesAccount = !contractAccountId || contract.accountId === contractAccountId
      const matchesTract = !tractId || contract.tractId === tractId
      const hasProduct = !productId || contract.products.some(p => p.productId === productId)
      return matchesDestination && matchesStatus && matchesSetting && matchesAccount && matchesTract && hasProduct
    })
  }

  removeDuplicateContracts (contracts) {
    const seen = new Set()
    return contracts.filter(contract => {
      if (seen.has(contract.contractId)) {
        return false
      }
      seen.add(contract)
      return true
    })
  }

  findContractResponse (contractId, tractId, contracts, ticketNumber, error) {
    return {
      contractId,
      tractId,
      contracts,
      ticketNumber,
      error: error || undefined
    }
  }

  getButtLogStatus (buttLogIndicator) {
    if (this.profile.buttLogIndicator) {
      return buttLogIndicator === this.profile.buttLogIndicator
    }
    const indicator = Number(buttLogIndicator)
    if (isNaN(indicator) || buttLogIndicator === '' || buttLogIndicator === null || buttLogIndicator === undefined) {
      return false
    }
    return Boolean(Number(indicator)).valueOf()
  }

  calculateVolumeWithFactor (volume) {
    if (this.profile.multiplicationFactor) {
      return volume * this.profile.multiplicationFactor
    }
    return volume
  }

  parseFromCsvProfile (scaleSheetCsvProfile) {
    const hasTwoSegments = scaleSheetCsvProfile.segmentTwoLength !== undefined &&
    scaleSheetCsvProfile.segmentTwoLength !== null &&
    scaleSheetCsvProfile.segmentTwoLength !== ''
    const hasThreeSegments = scaleSheetCsvProfile.segmentThreeLength !== undefined &&
    scaleSheetCsvProfile.segmentThreeLength !== null &&
    scaleSheetCsvProfile.segmentThreeLength !== ''
    const hasDefectVolumeSegmentOne = scaleSheetCsvProfile.segmentOneDefectVolume !== undefined &&
    scaleSheetCsvProfile.segmentOneDefectVolume !== null &&
    scaleSheetCsvProfile.segmentOneDefectVolume !== ''
    const hasDefectVolumeSegmentTwo = scaleSheetCsvProfile.segmentTwoDefectVolume !== undefined &&
    scaleSheetCsvProfile.segmentTwoDefectVolume !== null &&
    scaleSheetCsvProfile.segmentTwoDefectVolume !== ''
    const hasGrossVolumeSegmentOne = scaleSheetCsvProfile.segmentOneGrossVolume !== undefined &&
    scaleSheetCsvProfile.segmentOneGrossVolume !== null &&
    scaleSheetCsvProfile.segmentOneGrossVolume !== ''
    const hasGrossVolumeSegmentTwo = scaleSheetCsvProfile.segmentTwoGrossVolume !== undefined &&
    scaleSheetCsvProfile.segmentTwoGrossVolume !== null &&
    scaleSheetCsvProfile.segmentTwoGrossVolume !== ''
    const hasNetVolumeSegmentOne = scaleSheetCsvProfile.segmentOneNetVolume !== undefined &&
    scaleSheetCsvProfile.segmentOneNetVolume !== null &&
    scaleSheetCsvProfile.segmentOneNetVolume !== ''
    const hasNetVolumeSegmentTwo = scaleSheetCsvProfile.segmentTwoNetVolume !== undefined &&
    scaleSheetCsvProfile.segmentTwoNetVolume !== null &&
    scaleSheetCsvProfile.segmentTwoNetVolume !== ''
    const hasDestination = scaleSheetCsvProfile.destination !== undefined &&
    scaleSheetCsvProfile.destination !== null &&
    scaleSheetCsvProfile.destination !== ''
    const hasContractAccount = scaleSheetCsvProfile.contractAccount !== undefined &&
    scaleSheetCsvProfile.contractAccount !== null &&
    scaleSheetCsvProfile.contractAccount !== ''
    const hasSegmentOneGrade = scaleSheetCsvProfile.segmentOneGrade !== undefined &&
    scaleSheetCsvProfile.segmentOneGrade !== null &&
    scaleSheetCsvProfile.segmentOneGrade !== ''
    const hasSegmentTwoGrade = scaleSheetCsvProfile.segmentTwoGrade !== undefined &&
    scaleSheetCsvProfile.segmentTwoGrade !== null &&
    scaleSheetCsvProfile.segmentTwoGrade !== ''
    const hasSegmentOneEndUseSort = scaleSheetCsvProfile.segmentOneEndUseSort !== undefined &&
    scaleSheetCsvProfile.segmentOneEndUseSort !== null &&
    scaleSheetCsvProfile.segmentOneEndUseSort !== ''
    const hasSegmentTwoEndUseSort = scaleSheetCsvProfile.segmentTwoEndUseSort !== undefined &&
    scaleSheetCsvProfile.segmentTwoEndUseSort !== null &&
    scaleSheetCsvProfile.segmentTwoEndUseSort !== ''
    const hasProduct = scaleSheetCsvProfile.product !== undefined &&
    scaleSheetCsvProfile.product !== null &&
    scaleSheetCsvProfile.product !== ''
    const hasWeighedOutAt = scaleSheetCsvProfile.weighedOutAt !== undefined &&
    scaleSheetCsvProfile.weighedOutAt !== null &&
    scaleSheetCsvProfile.weighedOutAt !== ''
    const result = {
      ticketNumber: CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.ticketNumber,
        type: types.integer
      })),
      setting: scaleSheetCsvProfile.setting ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.setting
      })) : undefined,
      destination: hasDestination ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.destination
      })) : undefined,
      contractAccount: hasContractAccount ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.contractAccount
      })) : undefined,
      product: hasProduct ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.product
      })) : undefined,
      weighedOutAt: hasWeighedOutAt ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.weighedOutAt
      })) : undefined,
      tract: scaleSheetCsvProfile.tract ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.tract
      })) : undefined,
      inWeight: scaleSheetCsvProfile.inWeight ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.inWeight
      })) : undefined,
      outWeight: scaleSheetCsvProfile.outWeight ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.outWeight
      })) : undefined,
      netWeight: scaleSheetCsvProfile.netWeight ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.netWeight
      })) : undefined,
      trailerIdentifier: scaleSheetCsvProfile.trailerIdentifier ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.trailerIdentifier
      })) : undefined,
      species: CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.species
      })),
      segmentOneGrade: hasSegmentOneGrade ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.segmentOneGrade
      })) : undefined,
      segmentOneEndUseSort: hasSegmentOneEndUseSort ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.segmentOneEndUseSort
      })) : undefined,
      segmentOneGrossVolume: hasGrossVolumeSegmentOne ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentOneGrossVolume,
        type: types.floatRoundedOptional(3)
      })) : undefined,
      segmentOneNetVolume: hasNetVolumeSegmentOne ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentOneNetVolume,
        type: types.floatRoundedOptional(3)
      })) : undefined,
      segmentOneDefectVolume: hasDefectVolumeSegmentOne ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentOneDefectVolume,
        type: types.floatRoundedOptional(3)
      })) : undefined,
      segmentOneLength: CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentOneLength,
        type: types.integerOptional
      })),
      segmentOneDiameter: CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentOneDiameter,
        type: types.integerOptional
      })),
      segmentTwoGrade: hasSegmentTwoGrade ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.segmentTwoGrade
      })) : undefined,
      segmentTwoEndUseSort: hasSegmentTwoEndUseSort ? CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.segmentTwoEndUseSort
      })) : undefined,
      segmentTwoLength: hasTwoSegments ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentTwoLength,
        type: types.integerOptional
      })) : undefined,
      segmentTwoDiameter: hasTwoSegments ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentTwoDiameter,
        type: types.integerOptional
      })) : undefined,
      segmentTwoGrossVolume: hasTwoSegments && hasGrossVolumeSegmentTwo ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentTwoGrossVolume,
        type: types.floatRoundedOptional(3)
      })) : undefined,
      segmentTwoNetVolume: hasTwoSegments && hasNetVolumeSegmentTwo ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentTwoNetVolume,
        type: types.floatRoundedOptional(3)
      })) : undefined,
      segmentTwoDefectVolume: hasTwoSegments && hasDefectVolumeSegmentTwo ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentTwoDefectVolume,
        type: types.floatRoundedOptional(3)
      })) : undefined,
      segmentThreeLength: hasThreeSegments ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentThreeLength,
        type: types.integerOptional
      })) : undefined,
      segmentThreeDiameter: hasThreeSegments ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentThreeDiameter,
        type: types.integerOptional
      })) : undefined,
      segmentThreeGrossVolume: hasThreeSegments ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentThreeGrossVolume,
        type: types.floatRoundedOptional(3)
      })) : undefined,
      segmentThreeNetVolume: hasThreeSegments ? CsvColumn.build(types => ({
        name: scaleSheetCsvProfile.segmentThreeNetVolume,
        type: types.floatRoundedOptional(3)
      })) : undefined,
      buttLog: CsvColumn.build(() => ({
        name: scaleSheetCsvProfile.buttLog
      }))
    }
    return Object.fromEntries(Object.entries(result).filter(([_, v]) => v))
  }

  parseFromDelimitedProfile (ticketContent) {
    const {
      delimiter,
      speciesIndex,
      segmentOneGradeIndex,
      segmentOneEndUseSortIndex,
      segmentOneLengthIndex,
      segmentOneDiameterIndex,
      segmentOneGrossVolumeIndex,
      segmentOneNetVolumeIndex,
      segmentOneDefectVolumeIndex,
      segmentTwoLengthIndex,
      segmentTwoDiameterIndex,
      segmentTwoGrossVolumeIndex,
      segmentTwoNetVolumeIndex,
      segmentTwoDefectVolumeIndex,
      segmentTwoGradeIndex,
      segmentTwoEndUseSortIndex,
      segmentThreeLengthIndex,
      segmentThreeDiameterIndex,
      segmentThreeGrossVolumeIndex,
      segmentThreeNetVolumeIndex,
      buttLogIndex
    } = this.profile.scaleSheetImportDelimitedProfile

    const hasTwoSegments = segmentTwoLengthIndex !== undefined && segmentTwoLengthIndex !== null && segmentTwoLengthIndex > 0
    const hasThreeSegments = segmentThreeLengthIndex !== undefined && segmentThreeLengthIndex !== null && segmentThreeLengthIndex > 0
    const hasDefectVolumeSegmentOne = segmentOneDefectVolumeIndex !== undefined && segmentOneDefectVolumeIndex !== null && segmentOneDefectVolumeIndex > 0
    const hasDefectVolumeSegmentTwo = segmentTwoDefectVolumeIndex !== undefined && segmentTwoDefectVolumeIndex !== null && segmentTwoDefectVolumeIndex > 0
    const hasGrossVolumeSegmentOne = segmentOneGrossVolumeIndex !== undefined && segmentOneGrossVolumeIndex !== null && segmentOneGrossVolumeIndex > 0
    const hasGrossVolumeSegmentTwo = segmentTwoGrossVolumeIndex !== undefined && segmentTwoGrossVolumeIndex !== null && segmentTwoGrossVolumeIndex > 0
    const hasSegmentOneGrade = segmentOneGradeIndex !== undefined && segmentOneGradeIndex !== null && segmentOneGradeIndex > 0
    const hasSegmentTwoGrade = segmentTwoGradeIndex !== undefined && segmentTwoGradeIndex !== null && segmentTwoGradeIndex > 0
    const hasSegmentOneEndUseSort = segmentOneEndUseSortIndex !== undefined && segmentOneEndUseSortIndex !== null && segmentOneEndUseSortIndex > 0
    const hasSegmentTwoEndUseSort = segmentTwoEndUseSortIndex !== undefined && segmentTwoEndUseSortIndex !== null && segmentTwoEndUseSortIndex > 0
    const hasSegmentOneNetVolume = segmentOneNetVolumeIndex !== undefined && segmentOneNetVolumeIndex !== null && segmentOneNetVolumeIndex > 0
    const hasSegmentTwoNetVolume = segmentTwoNetVolumeIndex !== undefined && segmentTwoNetVolumeIndex !== null && segmentTwoNetVolumeIndex > 0

    return Object.entries(groupBy(ticketContent.flatMap(content => {
      return content.ticketContent.length > 0 ? content.ticketContent.map((line, index) => {
        const splitLine = line.split(delimiter)
        return {
          ticketNumber: Number(content.ticketNumber),
          setting: this.fileStringSanitizer(content.setting),
          tract: this.fileStringSanitizer(content.tract),
          destination: this.fileStringSanitizer(content.destination),
          contractAccount: this.fileStringSanitizer(content.contractAccount),
          product: this.fileStringSanitizer(content.product),
          weighedOutAt: this.fileStringSanitizer(content.weighedOutAt),
          inWeight: this.fileStringSanitizer(content.inWeight),
          outWeight: this.fileStringSanitizer(content.outWeight),
          netWeight: this.fileStringSanitizer(content.netWeight),
          logNumber: index + 1,
          species: this.fileStringSanitizer(splitLine[speciesIndex - 1]),
          segmentOneGrade: hasSegmentOneGrade ? this.fileStringSanitizer(splitLine[segmentOneGradeIndex - 1]) : '',
          segmentOneEndUseSort: hasSegmentOneEndUseSort ? this.fileStringSanitizer(splitLine[segmentOneEndUseSortIndex - 1]) : '',
          segmentOneLength: Number(splitLine[segmentOneLengthIndex - 1]),
          segmentOneDiameter: Number(splitLine[segmentOneDiameterIndex - 1]),
          segmentOneGrossVolume: hasGrossVolumeSegmentOne ? Number(splitLine[segmentOneGrossVolumeIndex - 1]) : '',
          segmentOneNetVolume: hasSegmentOneNetVolume ? Number(splitLine[segmentOneNetVolumeIndex - 1]) : '',
          segmentOneDefectVolume: hasDefectVolumeSegmentOne ? Number(splitLine[segmentOneDefectVolumeIndex - 1]) : '',
          segmentTwoGrade: hasTwoSegments && hasSegmentTwoGrade ? this.fileStringSanitizer(splitLine[segmentTwoGradeIndex - 1]) : '',
          segmentTwoEndUseSort: hasTwoSegments && hasSegmentTwoEndUseSort ? this.fileStringSanitizer(splitLine[segmentTwoEndUseSortIndex - 1]) : '',
          segmentTwoLength: hasTwoSegments ? Number(splitLine[segmentTwoLengthIndex - 1]) : '',
          segmentTwoDiameter: hasTwoSegments ? Number(splitLine[segmentTwoDiameterIndex - 1]) : '',
          segmentTwoGrossVolume: hasTwoSegments && hasGrossVolumeSegmentTwo ? Number(splitLine[segmentTwoGrossVolumeIndex - 1]) : '',
          segmentTwoNetVolume: hasTwoSegments && hasSegmentTwoNetVolume ? Number(splitLine[segmentTwoNetVolumeIndex - 1]) : '',
          segmentTwoDefectVolume: hasTwoSegments && hasDefectVolumeSegmentTwo ? Number(splitLine[segmentTwoDefectVolumeIndex - 1]) : '',
          segmentThreeLength: hasThreeSegments ? Number(splitLine[segmentThreeLengthIndex - 1]) : '',
          segmentThreeDiameter: hasThreeSegments ? Number(splitLine[segmentThreeDiameterIndex - 1]) : '',
          segmentThreeGrossVolume: hasThreeSegments ? Number(splitLine[segmentThreeGrossVolumeIndex - 1]) : '',
          segmentThreeNetVolume: hasThreeSegments ? Number(splitLine[segmentThreeNetVolumeIndex - 1]) : '',
          buttLog: this.fileStringSanitizer(splitLine[buttLogIndex - 1]),
          trailerIdentifier: content.trailerIdentifier,
          hasScaleData: true
        }
      }) : {
        ticketNumber: Number(content.ticketNumber),
        setting: this.fileStringSanitizer(content.setting),
        tract: this.fileStringSanitizer(content.tract),
        destination: this.fileStringSanitizer(content.destination),
        contractAccount: this.fileStringSanitizer(content.contractAccount),
        product: this.fileStringSanitizer(content.product),
        weighedOutAt: this.fileStringSanitizer(content.weighedOutAt),
        inWeight: this.fileStringSanitizer(content.inWeight),
        outWeight: this.fileStringSanitizer(content.outWeight),
        netWeight: this.fileStringSanitizer(content.netWeight),
        trailerIdentifier: content.trailerIdentifier,
        hasScaleData: false
      }
    }), d => JSON.stringify([d.ticketNumber, d.tract, d.destination, d.setting, d.contractAccount, d.product]))).map(([jsonKey, ticketContent]) => {
      const [ticketNumber] = JSON.parse(jsonKey)
      return {
        ticketNumber,
        ticketContent
      }
    })
  }

  parseFromFixedLengthProfile (ticketContent) {
    const {
      segmentOneDiameterStart,
      segmentOneDiameterLength,
      segmentTwoDiameterStart,
      segmentTwoDiameterLength,
      segmentOneLengthStart,
      segmentOneLengthLength,
      segmentTwoLengthStart,
      segmentTwoLengthLength,
      segmentOneGradeStart,
      segmentOneGradeLength,
      segmentOneEndUseSortStart,
      segmentOneEndUseSortLength,
      segmentOneGrossVolumeStart,
      segmentOneGrossVolumeLength,
      segmentTwoGrossVolumeStart,
      segmentTwoGrossVolumeLength,
      segmentOneNetVolumeStart,
      segmentOneNetVolumeLength,
      segmentOneDefectVolumeStart,
      segmentOneDefectVolumeLength,
      segmentTwoGradeStart,
      segmentTwoGradeLength,
      segmentTwoEndUseSortStart,
      segmentTwoEndUseSortLength,
      segmentTwoNetVolumeStart,
      segmentTwoNetVolumeLength,
      segmentTwoDefectVolumeStart,
      segmentTwoDefectVolumeLength,
      segmentThreeLengthStart,
      segmentThreeLengthLength,
      segmentThreeDiameterStart,
      segmentThreeDiameterLength,
      segmentThreeGrossVolumeStart,
      segmentThreeGrossVolumeLength,
      segmentThreeNetVolumeStart,
      segmentThreeNetVolumeLength,
      speciesStart,
      speciesLength,
      buttLogStart,
      buttLogLength
    } = this.profile.scaleSheetImportFixedLengthProfile

    const hasTwoSegments = segmentTwoLengthStart !== undefined && segmentTwoLengthStart !== null && segmentTwoLengthStart > 0
    const hasGrossVolumeSegmentOne = segmentOneGrossVolumeStart !== undefined && segmentOneGrossVolumeStart !== null && segmentOneGrossVolumeStart > 0 &&
      segmentOneGrossVolumeLength !== undefined && segmentOneGrossVolumeLength !== null && segmentOneGrossVolumeLength > 0
    const hasGrossVolumeSegmentTwo = segmentTwoGrossVolumeStart !== undefined && segmentTwoGrossVolumeStart !== null && segmentTwoGrossVolumeStart > 0 &&
      segmentTwoGrossVolumeLength !== undefined && segmentTwoGrossVolumeLength !== null && segmentTwoGrossVolumeLength > 0
    const hasNetVolumeSegmentOne = segmentOneNetVolumeStart !== undefined && segmentOneNetVolumeStart !== null && segmentOneNetVolumeStart > 0 &&
      segmentOneNetVolumeLength !== undefined && segmentOneNetVolumeLength !== null && segmentOneNetVolumeLength > 0
    const hasNetVolumeSegmentTwo = segmentTwoNetVolumeStart !== undefined && segmentTwoNetVolumeStart !== null && segmentTwoNetVolumeStart > 0 &&
      segmentTwoNetVolumeLength !== undefined && segmentTwoNetVolumeLength !== null && segmentTwoNetVolumeLength > 0
    const hasDefectVolumeSegmentOne = segmentOneDefectVolumeStart !== undefined && segmentOneDefectVolumeStart !== null && segmentOneDefectVolumeStart > 0 &&
      segmentOneDefectVolumeLength !== undefined && segmentOneDefectVolumeLength !== null && segmentOneDefectVolumeLength > 0
    const hasDefectVolumeSegmentTwo = segmentTwoDefectVolumeStart !== undefined && segmentTwoDefectVolumeStart !== null && segmentTwoDefectVolumeStart > 0 &&
      segmentTwoDefectVolumeLength !== undefined && segmentTwoDefectVolumeLength !== null && segmentTwoDefectVolumeLength > 0
    const hasThreeSegments = segmentThreeLengthStart !== undefined && segmentThreeLengthStart !== null && segmentThreeLengthStart > 0 &&
      segmentThreeDiameterStart !== undefined && segmentThreeDiameterStart !== null && segmentThreeDiameterStart > 0 &&
      segmentThreeGrossVolumeStart !== undefined && segmentThreeGrossVolumeStart !== null && segmentThreeGrossVolumeStart > 0 &&
      segmentThreeNetVolumeStart !== undefined && segmentThreeNetVolumeStart !== null && segmentThreeNetVolumeStart > 0
    const hasSegmentOneGrade = segmentOneGradeStart !== undefined && segmentOneGradeStart !== null && segmentOneGradeStart > 0 &&
      segmentOneGradeLength !== undefined && segmentOneGradeLength !== null && segmentOneGradeLength > 0
    const hasSegmentOneEndUseSort = segmentOneEndUseSortStart !== undefined && segmentOneEndUseSortStart !== null && segmentOneEndUseSortStart > 0 &&
      segmentOneEndUseSortLength !== undefined && segmentOneEndUseSortLength !== null && segmentOneEndUseSortLength > 0
    const hasSegmentTwoGrade = segmentTwoGradeStart !== undefined && segmentTwoGradeStart !== null && segmentTwoGradeStart > 0 &&
      segmentTwoGradeLength !== undefined && segmentTwoGradeLength !== null && segmentTwoGradeLength > 0
    const hasSegmentTwoEndUseSort = segmentTwoEndUseSortStart !== undefined && segmentTwoEndUseSortStart !== null && segmentTwoEndUseSortStart > 0 &&
      segmentTwoEndUseSortLength !== undefined && segmentTwoEndUseSortLength !== null && segmentTwoEndUseSortLength > 0

    return Object.entries(groupBy(ticketContent.flatMap(content => {
      return content.ticketContent.length > 0 ? content.ticketContent.map((line, index) => {
        return {
          ticketNumber: Number(content.ticketNumber),
          setting: this.fileStringSanitizer(content.setting),
          tract: this.fileStringSanitizer(content.tract),
          destination: this.fileStringSanitizer(content.destination),
          contractAccount: this.fileStringSanitizer(content.contractAccount),
          product: this.fileStringSanitizer(content.product),
          weighedOutAt: this.fileStringSanitizer(content.weighedOutAt),
          inWeight: this.fileStringSanitizer(content.inWeight),
          outWeight: this.fileStringSanitizer(content.outWeight),
          netWeight: this.fileStringSanitizer(content.netWeight),
          logNumber: index + 1,
          species: this.fileStringSanitizer(line.substring(speciesStart - 1, speciesStart + speciesLength - 1)),
          segmentOneGrade: hasSegmentOneGrade ? this.fileStringSanitizer(line.substring(segmentOneGradeStart - 1, segmentOneGradeStart + segmentOneGradeLength - 1)) : '',
          segmentOneEndUseSort: hasSegmentOneEndUseSort ? this.fileStringSanitizer(line.substring(segmentOneEndUseSortStart - 1, segmentOneEndUseSortStart + segmentOneEndUseSortLength - 1)) : '',
          segmentOneLength: Number(line.substring(segmentOneLengthStart - 1, segmentOneLengthStart + segmentOneLengthLength - 1).trim()),
          segmentOneDiameter: Number(line.substring(segmentOneDiameterStart - 1, segmentOneDiameterStart + segmentOneDiameterLength - 1).trim()),
          segmentOneGrossVolume: hasGrossVolumeSegmentOne ? Number(line.substring(segmentOneGrossVolumeStart - 1, segmentOneGrossVolumeStart + segmentOneGrossVolumeLength - 1).trim()) : '',
          segmentOneNetVolume: hasNetVolumeSegmentOne ? Number(line.substring(segmentOneNetVolumeStart - 1, segmentOneNetVolumeStart + segmentOneNetVolumeLength - 1).trim()) : '',
          segmentOneDefectVolume: hasDefectVolumeSegmentOne ? Number(line.substring(segmentOneDefectVolumeStart - 1, segmentOneDefectVolumeStart + segmentOneDefectVolumeLength - 1).trim()) : '',
          segmentTwoGrade: hasTwoSegments && hasSegmentTwoGrade ? this.fileStringSanitizer(line.substring(segmentTwoGradeStart - 1, segmentTwoGradeStart + segmentTwoGradeLength - 1)) : '',
          segmentTwoEndUseSort: hasTwoSegments && hasSegmentTwoEndUseSort ? this.fileStringSanitizer(line.substring(segmentTwoEndUseSortStart - 1, segmentTwoEndUseSortStart + segmentTwoEndUseSortLength - 1)) : '',
          segmentTwoLength: hasTwoSegments ? Number(line.substring(segmentTwoLengthStart - 1, segmentTwoLengthStart + segmentTwoLengthLength - 1).trim()) : '',
          segmentTwoDiameter: hasTwoSegments ? Number(line.substring(segmentTwoDiameterStart - 1, segmentTwoDiameterStart + segmentTwoDiameterLength - 1).trim()) : '',
          segmentTwoGrossVolume: hasTwoSegments && hasGrossVolumeSegmentTwo ? Number(line.substring(segmentTwoGrossVolumeStart - 1, segmentTwoGrossVolumeStart + segmentTwoGrossVolumeLength - 1).trim()) : '',
          segmentTwoNetVolume: hasTwoSegments && hasNetVolumeSegmentTwo ? Number(line.substring(segmentTwoNetVolumeStart - 1, segmentTwoNetVolumeStart + segmentTwoNetVolumeLength - 1).trim()) : '',
          segmentTwoDefectVolume: hasTwoSegments && hasDefectVolumeSegmentTwo ? Number(line.substring(segmentTwoDefectVolumeStart - 1, segmentTwoDefectVolumeStart + segmentTwoDefectVolumeLength - 1).trim()) : '',
          segmentThreeLength: hasThreeSegments ? Number(line.substring(segmentThreeLengthStart - 1, segmentThreeLengthStart + segmentThreeLengthLength - 1).trim()) : '',
          segmentThreeDiameter: hasThreeSegments ? Number(line.substring(segmentThreeDiameterStart - 1, segmentThreeDiameterStart + segmentThreeDiameterLength - 1).trim()) : '',
          segmentThreeGrossVolume: hasThreeSegments ? Number(line.substring(segmentThreeGrossVolumeStart - 1, segmentThreeGrossVolumeStart + segmentThreeGrossVolumeLength - 1).trim()) : '',
          segmentThreeNetVolume: hasThreeSegments ? Number(line.substring(segmentThreeNetVolumeStart - 1, segmentThreeNetVolumeStart + segmentThreeNetVolumeLength - 1).trim()) : '',
          buttLog: this.fileStringSanitizer(line.substring(buttLogStart - 1, buttLogStart + buttLogLength - 1)),
          trailerIdentifier: content.trailerIdentifier,
          hasScaleData: true
        }
      }) : {
        ticketNumber: Number(content.ticketNumber),
        setting: this.fileStringSanitizer(content.setting),
        tract: this.fileStringSanitizer(content.tract),
        destination: this.fileStringSanitizer(content.destination),
        contractAccount: this.fileStringSanitizer(content.contractAccount),
        product: this.fileStringSanitizer(content.product),
        weighedOutAt: this.fileStringSanitizer(content.weighedOutAt),
        inWeight: this.fileStringSanitizer(content.inWeight),
        outWeight: this.fileStringSanitizer(content.outWeight),
        netWeight: this.fileStringSanitizer(content.netWeight),
        trailerIdentifier: content.trailerIdentifier,
        hasScaleData: false
      }
    }), d => JSON.stringify([d.ticketNumber, d.tract, d.destination, d.setting, d.contractAccount, d.product]))).map(([jsonKey, ticketContent]) => {
      const [ticketNumber] = JSON.parse(jsonKey)
      return {
        ticketNumber,
        ticketContent
      }
    })
  }

  getTicketDataForDelimitedData () {
    const {
      delimiter,
      headerPrefix,
      virtualPrefix,
      ticketNumberIndex,
      settingIndex,
      tractIndex,
      destinationIndex,
      contractAccountIndex,
      productIndex,
      weighedOutAtIndex,
      inWeightIndex,
      outWeightIndex,
      netWeightIndex,
      trailerIdentifierIndex
    } = this.profile.scaleSheetImportDelimitedProfile

    const scaleDataStringArray = this.scaleSheetString
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)

    // A virtual prefix identifies header lines by their first character; otherwise the entire first field must equal the header prefix.
    const isHeaderLine = virtualPrefix
      ? line => line.charAt(0) === virtualPrefix
      : line => line.split(delimiter)[0].trim() === headerPrefix

    const hasContractAccount = contractAccountIndex !== undefined && contractAccountIndex !== null && contractAccountIndex > 0
    const hasDestination = destinationIndex !== undefined && destinationIndex !== null && destinationIndex > 0
    const hasProduct = productIndex !== undefined && productIndex !== null && productIndex > 0
    const hasWeighedOutAt = weighedOutAtIndex !== undefined && weighedOutAtIndex !== null && weighedOutAtIndex > 0

    const tickets = []
    let currentIndex = 0

    while (currentIndex < scaleDataStringArray.length) {
      const startIdx = scaleDataStringArray.slice(currentIndex).findIndex(isHeaderLine)

      if (startIdx === -1) {
        break
      }

      const actualStartIdx = currentIndex + startIdx
      const headerLine = scaleDataStringArray[actualStartIdx]
      const splitHeaderLine = headerLine.split(delimiter)
      const ticketNumber = splitHeaderLine[ticketNumberIndex - 1]
      const setting = splitHeaderLine[settingIndex - 1]
      const tract = splitHeaderLine[tractIndex - 1]
      const destination = hasDestination ? splitHeaderLine[destinationIndex - 1] : undefined
      const contractAccount = hasContractAccount ? splitHeaderLine[contractAccountIndex - 1] : undefined
      const product = hasProduct ? splitHeaderLine[productIndex - 1] : undefined
      const weighedOutAt = hasWeighedOutAt ? splitHeaderLine[weighedOutAtIndex - 1] : undefined
      const inWeight = splitHeaderLine[inWeightIndex - 1]
      const outWeight = splitHeaderLine[outWeightIndex - 1]
      const netWeight = splitHeaderLine[netWeightIndex - 1]
      const trailerIdentifier = trailerIdentifierIndex ? splitHeaderLine[trailerIdentifierIndex - 1] : undefined

      const endIdx = scaleDataStringArray.slice(actualStartIdx + 1).findIndex(isHeaderLine)

      const ticketContent = endIdx !== -1 && endIdx !== 0
        ? scaleDataStringArray.slice(actualStartIdx + 1, actualStartIdx + 1 + endIdx)
        : endIdx === -1
          ? scaleDataStringArray.slice(actualStartIdx + 1)
          : []

      tickets.push({
        ticketNumber: ticketNumber,
        setting: setting,
        tract: tract,
        destination: destination,
        contractAccount: contractAccount,
        product: product,
        weighedOutAt: weighedOutAt,
        inWeight: inWeight,
        outWeight: outWeight,
        netWeight: netWeight,
        trailerIdentifier: trailerIdentifier,
        ticketContent: ticketContent
      })

      currentIndex = endIdx !== -1 ? actualStartIdx + 1 + endIdx : scaleDataStringArray.length
    }

    if (tickets.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInDelimitedFile'))
      return undefined
    }

    return tickets
  }

  async getTicketDataForScaleSheetFixedLength () {
    const scaleSheetStrings = this.scaleSheetString.split('\n').map(line => line.trim()).filter(line => line.length > 0)
    if (scaleSheetStrings.length === 0) {
      store.dispatch('snackbar/setSnackError', i18n.t('noValidTicketDataFoundInFixedLengthFile'))
      return undefined
    }

    const {
      skipTop,
      skipBottom,
      skipBottomExpanded,
      readUntil,
      ticketNumberLine,
      ticketNumberStart,
      ticketNumberLength,
      settingLine,
      settingStart,
      settingLength,
      tractLine,
      tractStart,
      tractLength,
      destinationLine,
      destinationStart,
      destinationLength,
      contractAccountLine,
      contractAccountStart,
      contractAccountLength,
      productLine,
      productStart,
      productLength,
      weighedOutAtLine,
      weighedOutAtStart,
      weighedOutAtLength,
      inWeightLine,
      inWeightStart,
      inWeightLength,
      outWeightLine,
      outWeightStart,
      outWeightLength,
      netWeightLine,
      netWeightStart,
      netWeightLength,
      trailerIdentifierLine,
      trailerIdentifierStart,
      trailerIdentifierLength
    } = this.profile.scaleSheetImportFixedLengthProfile

    const lineEndsWithReadUntil = skipBottom === 0 ? 1 : 0
    const skipBottomExpansionLoad = skipBottomExpanded ?? skipBottom
    const hasDestination = destinationLine !== undefined && destinationLine !== null && destinationLine > 0 &&
      destinationStart !== undefined && destinationStart !== null && destinationStart > 0 &&
      destinationLength !== undefined && destinationLength !== null && destinationLength > 0
    const hasContractAccount = contractAccountLine !== undefined && contractAccountLine !== null && contractAccountLine > 0 &&
      contractAccountStart !== undefined && contractAccountStart !== null && contractAccountStart > 0 &&
      contractAccountLength !== undefined && contractAccountLength !== null && contractAccountLength > 0
    const hasProduct = productLine !== undefined && productLine !== null && productLine > 0 &&
      productStart !== undefined && productStart !== null && productStart > 0 &&
      productLength !== undefined && productLength !== null && productLength > 0
    const hasWeighedOutAt = weighedOutAtLine !== undefined && weighedOutAtLine !== null && weighedOutAtLine > 0 &&
      weighedOutAtStart !== undefined && weighedOutAtStart !== null && weighedOutAtStart > 0 &&
      weighedOutAtLength !== undefined && weighedOutAtLength !== null && weighedOutAtLength > 0

    const ticketIndexes = []
    let currentTicketIndex = 0
    for (let i = skipTop; i < scaleSheetStrings.length; i++) {
      const line = scaleSheetStrings[i]
      const doneReadingTicket = readUntil.length === 1 ? line[0] === readUntil : line.includes(readUntil)
      if (doneReadingTicket || i === scaleSheetStrings.length - 1) {
        ticketIndexes.push({
          currentTicketIndex,
          endIndex: i
        })
        if (i - currentTicketIndex - skipTop <= 0) {
          i += skipBottomExpansionLoad - lineEndsWithReadUntil
        } else {
          i += skipBottom - lineEndsWithReadUntil
        }
        currentTicketIndex = i + 1
        i += skipTop
      }
    }
    return ticketIndexes.map(indexes => {
      const ticketData = scaleSheetStrings.slice(indexes.currentTicketIndex, indexes.endIndex)
      const ticketNumber = ticketData[ticketNumberLine - 1].substring(ticketNumberStart - 1, ticketNumberStart + ticketNumberLength)
      const setting = settingLine !== undefined && settingLine !== null ? ticketData[settingLine - 1].substring(settingStart - 1, settingStart + settingLength) : undefined
      const tract = tractLine !== undefined && tractLine !== null ? ticketData[tractLine - 1].substring(tractStart - 1, tractStart + tractLength) : undefined
      const destination = hasDestination ? ticketData[destinationLine - 1].substring(destinationStart - 1, destinationStart + destinationLength) : undefined
      const contractAccount = hasContractAccount ? ticketData[contractAccountLine - 1].substring(contractAccountStart - 1, contractAccountStart + contractAccountLength) : undefined
      const product = hasProduct ? ticketData[productLine - 1].substring(productStart - 1, productStart + productLength) : undefined
      const weighedOutAt = hasWeighedOutAt ? ticketData[weighedOutAtLine - 1].substring(weighedOutAtStart - 1, weighedOutAtStart + weighedOutAtLength) : undefined
      const inWeight = inWeightLine !== undefined && inWeightLine !== null ? ticketData[inWeightLine - 1].substring(inWeightStart - 1, inWeightStart + inWeightLength) : undefined
      const outWeight = outWeightLine !== undefined && outWeightLine !== null ? ticketData[outWeightLine - 1].substring(outWeightStart - 1, outWeightStart + outWeightLength) : undefined
      const netWeight = ticketData[netWeightLine - 1].substring(netWeightStart - 1, netWeightStart + netWeightLength)
      const trailerIdentifier = trailerIdentifierLine !== undefined && trailerIdentifierLine !== null
        ? ticketData[trailerIdentifierLine - 1].substring(trailerIdentifierStart - 1, trailerIdentifierStart + trailerIdentifierLength)
        : undefined
      const segments = ticketData.slice(skipTop)
      return {
        ticketNumber: ticketNumber,
        setting: setting,
        tract: tract,
        destination: destination,
        contractAccount: contractAccount,
        product: product,
        weighedOutAt: weighedOutAt,
        inWeight: inWeight,
        outWeight: outWeight,
        netWeight: netWeight,
        trailerIdentifier: trailerIdentifier,
        ticketContent: segments
      }
    })
  }

  fileStringSanitizer = (fileString) => fileString?.replace(/['"]/g, '')?.trim()

  defaultTrailerIdentifier = () => this.profile.defaultTrailerIdentifier || this.profile.name.substring(0, NAME_TRAILER_IDENTIFIER_INDEX).trim()
}
